//gemini changes - 3 Octoober 2024 3:16

import React, { useState, useEffect, useRef, useCallback } from "react";
import Papa from "papaparse";
import "./styles.css";
import QRCode from "react-qr-code";

import { auth, db, storage } from "./firebase";
import { usedAtVenueRecently } from "./venues";
import { onAuthStateChanged } from "firebase/auth";
import {
  FREE_TEST_QUESTION_LIMIT,
  getQuizTokenBalance,
  isLiveHostMode,
  isTestHostMode,
  wouldExceedFreeTestLimit,
} from "./utils/entitlements";
import { loadQuestionCounts } from "./utils/quizQuestionCount";
import { spendQuizToken } from "./lib/billingApi";
import {
  buildVanishingChoicePlan,
  computeVanishingIndexesFromPlan,
  finalizeVanishingIndexes,
  shouldDeferTimerUntilAnswersGate,
  timerActiveOnQuestionAdvance,
  getQuestionDurationSeconds,
} from "./quizHelpers";

// Firestore
import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  collection,
  getDoc,
  getDocs,
  writeBatch,
  serverTimestamp,
  query,
  where,
  deleteDoc,
  orderBy, // <-- Add if missing
  limit,   // <-- Add if missing
  arrayUnion, // <-- Add if missing
  startAfter,
  runTransaction
} from "firebase/firestore";

// Storage
import {
  ref,
  uploadBytes,
  uploadString,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
} from "firebase/storage";

import Scores from "./Scores";
import QuizSelector from "./QuizSelector";
import QuizScreen from "./Quizscreen";
import QuizmasterLogin from "./QuizmasterLogin";
import ManageQuizzes from "./ManageQuizzes";
import TopTeamsPopup from "./TopTeamsPopup";
import QuestionScores from "./questionscores";

import Reconnect from "./Reconnect";
import Menu from "./Menu";
import { addTestAnswersForQuestion, testTeams } from "./TestDataGenerator";
import { APP_VERSION, APP_BUILD_DATE } from "./version";
import {
  getExtraTeamChoiceTagForPlaceholder,
  teamRoundChoiceSettingsForGameDoc,
  runPreflightTeamTagPools,
  csvHasTeamChoiceMarkers,
  getTeamRoundChoiceSettingsFromLocalStorage,
  libraryIdsUsedInGame,
} from "./roundTagChoice";

import AnswerReveal from "./AnswerReveal";
import { appConfirm, appAlert } from "./components/AppDialog";
//import TeamsIntroScroll from "./TeamsIntroScroll"; // <--- ADD THIS LINE

function extractHashtagsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const trimmed = text.trim();
  if (!trimmed.startsWith("#")) return [];
  if (trimmed.includes("\n")) return [];
  const positions = [];
  for (let i = 0; i < trimmed.length; i += 1) {
    if (trimmed[i] === "#") positions.push(i);
  }
  const tags = [];
  for (let i = 0; i < positions.length; i += 1) {
    const start = positions[i] + 1;
    const end = i + 1 < positions.length ? positions[i + 1] : trimmed.length;
    const raw = trimmed.slice(start, end).trim();
    if (raw) tags.push(raw);
  }
  return tags;
}

function normalizeTagValue(raw) {
  if (!raw) return "";
  return String(raw).trim().replace(/^#+/, "").toLowerCase();
}

function normalizeTagList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.flatMap((entry) => parseTagsFromString(entry));
  }
  if (typeof raw === "string") {
    return parseTagsFromString(raw);
  }
  return [];
}

function parseTagsFromString(raw) {
  if (!raw) return [];
  const text = String(raw);
  const hashtagMatches = text.match(/#[^\s,]+/g);
  if (hashtagMatches && hashtagMatches.length > 0) {
    return hashtagMatches.map(normalizeTagValue).filter(Boolean);
  }
  return text
    .split(/[,\s]+/g)
    .map(normalizeTagValue)
    .filter(Boolean);
}

/** Host quiz-list action buttons: compact, consistent "real button" look (hover styles in .qm-action-btn). */
const QUIZ_ACTION_COLORS = {
  host: ["#43e08a", "#1d9e57", "#08321b"],
  edit: ["#ffd75e", "#e8a900", "#3a2a00"],
  delete: ["#ff7070", "#d63333", "#ffffff"],
  upload: ["#5db2ff", "#1f78e0", "#ffffff"],
  share: ["#ffad4d", "#e47a00", "#ffffff"],
  newQuiz: ["#8e9bff", "#5363ea", "#ffffff"],
  questions: ["#b183ff", "#7a3cf0", "#ffffff"],
  library: ["#3fd08a", "#1d9a5c", "#ffffff"],
};
function quizActionButtonStyle(kind, enabled) {
  const [top, bottom, text] = QUIZ_ACTION_COLORS[kind] || QUIZ_ACTION_COLORS.upload;
  const primary = kind === "host";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    height: primary ? 44 : 38,
    padding: primary ? "0 26px" : "0 16px",
    minWidth: primary ? 150 : 0,
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,0.28)",
    background: `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`,
    color: text,
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    fontWeight: 800,
    fontSize: primary ? "1rem" : "0.88rem",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), 0 3px 10px rgba(0,0,0,0.35)",
    textShadow: text === "#ffffff" ? "0 1px 1px rgba(0,0,0,0.3)" : "none",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.38,
    filter: enabled ? "none" : "grayscale(0.5)",
    transition: "transform .12s ease, filter .12s ease, box-shadow .12s ease",
  };
}

/** Host Quiz list columns: Quiz name | Owner | Questions (header and rows share this). */
const QUIZ_LIST_COLUMNS = "minmax(0, 1fr) 150px 110px";

/** CATs where leading # in Q is title/instructions only (e.g. STARTROUND #TeamChoice) — never QuestionLibrary placeholders */
const NO_LIBRARY_PLACEHOLDER_CATS = new Set([
  "STARTROUND",
  "ENDROUND",
  "SHOWROUNDS",
  "JOIN QUIZ",
  "INTRODUCTION",
  "END QUIZ",
  "BREAK TIME",
  "DISPLAY",
  "SLIDE",
  "DEFAULT",
  "DEFAULT CATEGORY",
]);

// Delete a quiz from Storage and Firestore (including /shares subcollection)
async function deleteQuizEverywhere(fileObj) {
  if (!fileObj || !fileObj.name) return;

  const fileName = fileObj.name;                    // e.g. "Romcoms.csv"
  const storageRefForFile = ref(storage, `quizzes/${fileName}`);
  const quizDocRef = doc(db, "quizzes", fileName);

  // Try to remove Firestore items first (ok if they don't exist)
  try {
    // Delete any nested /shares docs (Firestore doesn't auto-delete subcollections)
    const sharesColRef = collection(quizDocRef, "shares");
    const sharesSnap = await getDocs(sharesColRef);
    if (!sharesSnap.empty) {
      const batch = writeBatch(db);
      sharesSnap.forEach((s) => batch.delete(doc(quizDocRef, "shares", s.id)));
      await batch.commit();
    }

    // Delete the quiz document itself
    await deleteDoc(quizDocRef);
  } catch (err) {
    console.warn("Firestore delete skipped/failed:", err?.message || err);
  }

  // Delete the CSV in Storage (ok if it was already gone)
  try {
    // Prefer the ref we already have on the list item, fall back to path
    await deleteObject(fileObj.ref || storageRefForFile);
  } catch (err) {
    console.warn("Storage delete skipped/failed:", err?.message || err);
  }

  // Update UI
  setQuizFiles((prev) => prev.filter((f) => f.name !== fileName));
  setSelectedQuiz(null);
}


function DelayedQuestionScoresGate({ show, idx, gameCode, onCloseAndAdvance, roundQuestionStartIdx, questions }) {
  const [ready, setReady] = React.useState(false);

  // Lock auto-advance immediately
  React.useEffect(() => {
    try { window.__overlayActive = !!show; } catch {}
    return () => { try { window.__overlayActive = false; } catch {} };
  }, [show]);

  // +3s dwell on the underlying slide before mounting the Scores component
  React.useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setReady(true), 3000);
    return () => clearTimeout(t);
  }, [show]);

  if (!show || !ready) return null;

  return (
    <QuestionScores
      questionIdx={idx}
      gameCode={gameCode}
      questions={questions} // Pass full question array for type detection
      roundQuestionStartIdx={roundQuestionStartIdx} // <--- NEW PROP
      // The scores component will call this when its internal timer finishes (or if host closes early)
      onClose={() => {
        // Release lock, hide overlay, then trigger the normal advance signal.
        try { window.__overlayActive = false; } catch {}
        try { onCloseAndAdvance(); } catch {}
        try { window.dispatchEvent(new CustomEvent("quiz:advance")); } catch {}
      }}
    />
  );
}


// Fetch quizzes I own or are shared with me (Firestore source of truth)
// ==============================
// Fetch quizzes I own or are shared with me (Firestore + Storage fallback)
// Fast loader: Firestore (owned + shared) + Storage fallback in parallel
// Fast loader with normalised timestamps (ms) and consistent ownerUid
// Fast loader with normalised timestamps (ms) + owner displayName lookup
// Fast loader with normalised timestamps (ms) + owner displayName lookup
// Fast loader with owner displayName resolution (Firestore + Storage)
// Fast loader with reliable owner detection + displayName lookup
// Strict loader: only my owned quizzes + quizzes shared with me.
// Includes a Storage fallback ONLY for my own files missing a Firestore doc.
// Strict loader: only my owned quizzes + quizzes shared with me.
// Uses the CSV file's Storage metadata.updated (last modified) for "Last updated".
// Strict loader: only my owned quizzes + quizzes shared with me.
// "Last updated" comes from the CSV's Storage metadata.updated.
// NEW: non-blocking backfill to set Storage customMetadata.ownerUid for my files if missing.


// Quizmaster.js — helper: treat SHOWROUNDS like a display-style slide for dwell & numbering
function isDisplayLikeSlide(q) {
  if (!q) return false;
  const rawType = String(q.type ?? q.CAT ?? "").toUpperCase();

  // Canonical display-likes
  if (
    rawType === "DISPLAY" ||
    rawType === "SLIDE" ||
    rawType === "STARTROUND" ||
    rawType === "ENDROUND" ||
    rawType === "SHOWROUNDS" || // <<< important
    rawType === "JOIN QUIZ" ||
    rawType === "INTRODUCTION" ||
    rawType === "END QUIZ" ||
    rawType === "BREAK TIME"
  ) {
    return true;
  }

  // Content heuristic: no answers and zero time behaves like a slide
  const answersArr = Array.isArray(q.answers) ? q.answers : [];
  const timeVal = parseInt(q.time ?? q.TIME ?? 0, 10) || 0;
  const noAnswers = answersArr.every(a => String(a ?? "").trim() === "");
  if (noAnswers && timeVal === 0) return true;

  return false;
}


async function fetchQuizzesIncludingShares(currentUser) {
  if (!currentUser) return [];
  const uid = currentUser.uid;
  const email = currentUser.email; // Get the user's email

  // --- HARDCODED ADMIN CHECK ---
  // If the logged-in user's email is one of these, they see all quizzes.
  const isHardcodedAdmin =
    email === 'rob@izeus.org' ||
    email === 'suelaw42@gmail.com';

  const quizzesCol = collection(db, "quizzes");

  if (isHardcodedAdmin) {
    // --- ADMIN PATH: Fetch ALL quizzes ---
    const allQuizzesSnap = await getDocs(quizzesCol);
    const ownedIds = new Set();
    const merged = new Map();
    
    allQuizzesSnap.docs.forEach((d) => {
        merged.set(d.id, { id: d.id, ...d.data() });
        // Still track ownership to correctly display "Owner: You"
        if (d.data()?.ownerUid === uid) {
            ownedIds.add(d.id);
        }
    });

    // The rest of the logic is identical to the original, just applied to all quizzes.
    const names = [...merged.keys()];
    
    // 3) ALWAYS read Storage metadata for "Last updated"
    const metaByName = new Map();
    const fetched = await Promise.all(
      names.map(async (name) => {
        try { return { name, m: await getMetadata(ref(storage, `quizzes/${name}`)) }; }
        catch { return { name, m: null }; }
      })
    );
    fetched.forEach(({ name, m }) => metaByName.set(name, m));

    // 4) Build rows with correct data
    const rows = names.map((name) => {
      const qdoc = merged.get(name) || {};
      const m = metaByName.get(name) || null;
      const updated = (m?.updated ? Date.parse(m.updated) : null) ?? (qdoc?.updatedAt ? tsToMs(qdoc.updatedAt) : null) ?? null;
      let ownerUid = ownedIds.has(name) ? uid : (qdoc?.ownerUid || qdoc?.ownerUID || qdoc?.owner || m?.customMetadata?.ownerUid || m?.customMetadata?.owner || null);
      return { ref: ref(storage, `quizzes/${name}`), name, updated, ownerUid };
    });

    // 5) Resolve owner display names from users/<uid>
    const distinctOwners = Array.from(new Set(rows.map(r => r.ownerUid).filter(Boolean)));
    const nameMap = new Map();
    if (distinctOwners.length) {
      await Promise.all(distinctOwners.map(async (ouid) => {
        try {
          const snap = await getDoc(doc(db, "users", ouid));
          const u = snap.exists() ? snap.data() : null;
          const display = u?.displayName || u?.name || u?.profile?.displayName || u?.email || `${String(ouid).slice(0,8)}…`;
          nameMap.set(ouid, display);
        } catch {
          nameMap.set(ouid, `${String(ouid).slice(0,8)}…`);
        }
      }));
    }

    const finalRows = rows.map((r) => ({
      ...r,
      ownerName: r.ownerUid
        ? (r.ownerUid === uid ? "You" : (nameMap.get(r.ownerUid) || `${String(r.ownerUid).slice(0,8)}…`))
        : (ownedIds.has(r.name) ? "You" : "unknown"),
    }));

    // 6) Non-blocking backfill for missing Storage customMetadata.ownerUid
    try {
      const toStamp = names.filter((name) => {
        const qdoc = merged.get(name) || {};
        const m = metaByName.get(name) || {};
        const docOwner = qdoc.ownerUid || qdoc.ownerUID || qdoc.owner || null;
        const metaOwner = m?.customMetadata?.ownerUid || m?.customMetadata?.owner || null;
        return docOwner === uid && !metaOwner;
      });
      if (toStamp.length) {
        setTimeout(() => {
          Promise.allSettled(
            toStamp.map(async (name) => {
              const fileRef = ref(storage, `quizzes/${name}`);
              const existing = metaByName.get(name)?.customMetadata || {};
              await updateMetadata(fileRef, { customMetadata: { ...existing, ownerUid: uid } });
            })
          ).catch(() => {});
        }, 0);
      }
    } catch { /* ignore */ }

    finalRows.sort((a, b) => (b.updated || 0) - (a.updated || 0));
    return finalRows;

  } else {
    // --- NORMAL USER PATH: Keep original logic verbatim ---
    const ownedQ  = query(quizzesCol, where("ownerUid", "==", uid));
    const sharedQ = query(quizzesCol, where("sharedWithUids", "array-contains", uid));

    // 1) Firestore queries in parallel
    const [ownedSnap, sharedSnap] = await Promise.all([getDocs(ownedQ), getDocs(sharedQ)]);
    const ownedIds = new Set(ownedSnap.docs.map((d) => d.id));

    // Merge Firestore docs (owned + shared)
    const merged = new Map();
    [...ownedSnap.docs, ...sharedSnap.docs].forEach((d) => {
      merged.set(d.id, { id: d.id, ...d.data() });
    });

    // 2) Storage fallback ONLY for files not in Firestore that YOU own
    const fallbackOwned = new Map();
    try {
      const all = await listAll(ref(storage, "quizzes"));
      const candidates = all.items.filter((it) => !merged.has(it.name));
      const metas = await Promise.all(
        candidates.map(async (it) => {
          try { return { it, m: await getMetadata(it) }; }
          catch { return { it, m: null }; }
        })
      );
      for (const { it, m } of metas) {
        const owner = m?.customMetadata?.ownerUid || m?.customMetadata?.owner || null;
        if (owner === uid) fallbackOwned.set(it.name, { storagePath: it.fullPath, meta: m });
      }
    } catch { /* ignore */ }

    // Names to show = Firestore (owned+shared) + my Storage-owned fallbacks
    const names = [...new Set([...merged.keys(), ...fallbackOwned.keys()])];

    // 3) ALWAYS read Storage metadata for displayed files so "Last updated" is from the CSV
    const metaByName = new Map();
    for (const [name, obj] of fallbackOwned.entries()) metaByName.set(name, obj.meta);

    const missing = names.filter((n) => !metaByName.has(n));
    const fetched = await Promise.all(
      missing.map(async (name) => {
        try { return { name, m: await getMetadata(ref(storage, `quizzes/${name}`)) }; }
        catch { return { name, m: null }; }
      })
    );
    fetched.forEach(({ name, m }) => metaByName.set(name, m));

    // 4) Build rows. Prefer Storage m.updated (CSV last-modified). Fallback to Firestore if missing.
    const rows = names.map((name) => {
      const qdoc = merged.get(name) || {};
      const m = metaByName.get(name) || null;

      const updated =
        (m?.updated ? Date.parse(m.updated) : null) ??
        (qdoc?.updatedAt ? tsToMs(qdoc.updatedAt) : null) ??
        null;

      // If Firestore says you own it (via ownedIds), force ownerUid=uid
      let ownerUid = ownedIds.has(name) ? uid : null;
      ownerUid =
        ownerUid ||
        qdoc?.ownerUid || qdoc?.ownerUID || qdoc?.owner ||
        m?.customMetadata?.ownerUid || m?.customMetadata?.owner ||
        null;

      return { ref: ref(storage, `quizzes/${name}`), name, updated, ownerUid };
    });

    // 5) Resolve owner display names from users/<uid>
    const distinctOwners = Array.from(new Set(rows.map(r => r.ownerUid).filter(Boolean)));
    const nameMap = new Map();
    if (distinctOwners.length) {
      await Promise.all(distinctOwners.map(async (ouid) => {
        try {
          const snap = await getDoc(doc(db, "users", ouid));
          const u = snap.exists() ? snap.data() : null;
          const display = u?.displayName || u?.name || u?.profile?.displayName || u?.email || `${String(ouid).slice(0,8)}…`;
          nameMap.set(ouid, display);
        } catch {
          nameMap.set(ouid, `${String(ouid).slice(0,8)}…`);
        }
      }));
    }

    const finalRows = rows.map((r) => ({
      ...r,
      ownerName: r.ownerUid
        ? (r.ownerUid === uid ? "You" : (nameMap.get(r.ownerUid) || `${String(r.ownerUid).slice(0,8)}…`))
        : (ownedIds.has(r.name) ? "You" : "unknown"),
    }));

    // 6) 🔧 Non-blocking backfill for missing Storage customMetadata.ownerUid on YOUR files
    try {
      const toStamp = names.filter((name) => {
        const qdoc = merged.get(name) || {};
        const m = metaByName.get(name) || {};
        const docOwner = qdoc.ownerUid || qdoc.ownerUID || qdoc.owner || null;
        const metaOwner = m?.customMetadata?.ownerUid || m?.customMetadata?.owner || null;
        return docOwner === uid && !metaOwner; // only stamp your own files
      });

      if (toStamp.length) {
        setTimeout(() => {
          Promise.allSettled(
            toStamp.map(async (name) => {
              const fileRef = ref(storage, `quizzes/${name}`);
              const existing = metaByName.get(name)?.customMetadata || {};
              // Merge any existing custom metadata; just add ownerUid.
              await updateMetadata(fileRef, { customMetadata: { ...existing, ownerUid: uid } });
            })
          ).catch(() => {});
        }, 0);
      }
    } catch { /* ignore */ }

    finalRows.sort((a, b) => (b.updated || 0) - (a.updated || 0));
    return finalRows;
  }
}





const REQUIRED_FIELDS = [
  "CAT", "Q", "A2", "A3", "A4", "A5", "A6",
  "P1", "P2", "P3", "P4", "P5", "P6",
  "SOUND", "VIDEO", "BACKGROUND", "STYLE", "TIME", "NOTES"
];

// Quizmaster.js — helpers to normalise timestamps
// ─── Time helpers used by scoring (no Firestore import needed) ───────────────
function tsToMs(ts) {
  if (ts == null && ts !== 0) return null;          // null/undefined guard
  if (typeof ts === "number") return ts;            // already ms
  if (ts && typeof ts.toMillis === "function") {    // Firestore Timestamp-like
    return ts.toMillis();
  }
  if (ts && typeof ts.seconds === "number" && typeof ts.nanoseconds === "number") {
    // Plain Timestamp object shape { seconds, nanoseconds }
    return ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6);
  }
  return null;                                      // unsupported shape
}

function elapsedSecondsBetween(startTs, endTs) {
  const startMs = tsToMs(startTs);
  const endMs   = tsToMs(endTs);
  if (startMs == null || endMs == null) return null;
  return Math.max(0, Math.round((endMs - startMs) / 1000));
}


function ensurePreloadLink(url) {
  if (!url) return;
  const key = `preload-${url}`;
  if (document.querySelector(`link[data-preload="${key}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("data-preload", key);
  document.head.appendChild(link);
}


const autoSaveEditedQuiz = async (newQuestions) => {
  const csv = Papa.unparse(newQuestions);
  const storageRef = ref(storage, `quizzes/${editedQuizFile}`);
  await uploadString(storageRef, csv);
};



// Example: "game_1720004456284"
// Points for VANISHINGCHOICE depending on how many options were left


function getGlow(rank) {
  if (rank === 0) return "0 0 32px 8px #ffd700, 0 0 0 7px #fff8";
  if (rank === 1) return "0 0 32px 8px #c0c0c0, 0 0 0 7px #fff8";
  if (rank === 2) return "0 0 32px 8px #cd7f32, 0 0 0 7px #fff8";
  return "0 12px 64px #000b, 0 0 0 7px #fff4";
}
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

async function generateNextJoinCode() {
  const COUNTERS_COLLECTION = "gameCounters";
  const COUNTERS_DOC = "joinCodeCounter";
  
  try {
    const counterRef = doc(db, COUNTERS_COLLECTION, COUNTERS_DOC);
    
    // Use transaction to atomically read, increment, and write
    const result = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let codeToReturn;
      let codeToStore;
      
      if (!counterDoc.exists()) {
        // First time: return 1000, store 1001 for next time
        codeToReturn = 1000;
        codeToStore = 1001;
        transaction.set(counterRef, { lastCode: codeToStore });
      } else {
        // Get current code and increment
        const currentCode = counterDoc.data().lastCode || 1000;
        codeToReturn = currentCode;
        codeToStore = currentCode + 1;
        
        // Handle wrap-around: if we exceed 9999, wrap back to 1000
        if (codeToStore > 9999) {
          codeToStore = 1000;
        }
        
        transaction.update(counterRef, { lastCode: codeToStore });
      }
      
      return String(codeToReturn).padStart(4, "0");
    });
    
    return result;
  } catch (err) {
    console.error("Error generating join code:", err);
    // Fallback to random if transaction fails
    return String(Math.floor(1000 + Math.random() * 9000));
  }
}

function getSimilarity(a, b) {
  // Levenshtein distance-based similarity
  const dp = Array(a.length + 1).fill(null).map(() =>
    Array(b.length + 1).fill(null)
  );
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,     // deletion
        dp[i][j - 1] + 1,     // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }

  const distance = dp[a.length][b.length];
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - distance / maxLen;
}







// Quizmaster.js — score WHEEL (single correct) or TYPE (multiple correct alternatives)
// correctAnswerOrAnswers: string (one correct) or array of strings (any match counts)
async function scoreWheelQuestion(gameCode, current, correctAnswerOrAnswers, qObj) {
  const answersSnap = await getDocs(collection(db, "games", gameCode, "answers"));
  const teamAnswers = [];
  answersSnap.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.q === current && typeof data.answer === "string" && data.answer !== null) {
      teamAnswers.push({
        team: data.team,
        answer: data.answer,
        submittedAt: data.submittedAt || data.time || null,
        awarded: data.awarded,
        docId: docSnap.id,
      });
    }
  });

  function normalizeWheelAnswer(s) {
    return (s || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
  }
  const correctList = Array.isArray(correctAnswerOrAnswers)
    ? correctAnswerOrAnswers.map((t) => normalizeWheelAnswer(t))
    : [normalizeWheelAnswer(correctAnswerOrAnswers)];

  const gameDocSnap = await getDoc(doc(db, "games", gameCode));
  const gameData = gameDocSnap.data() || {};
  const startTs = gameData.questionStartTimeServer || gameData.questionStartTime || gameData.questionStartTimeLegacyMs;
  const totalSeconds = (qObj && typeof qObj.time === "number" && qObj.time > 0) ? qObj.time : 20;

  const maxPoints = typeof qObj.points === "number" && !isNaN(qObj.points)
    ? qObj.points
    : Number(qObj.points) || 20;

  const batch = writeBatch(db);

  teamAnswers.forEach((a) => {
    const normalized = normalizeWheelAnswer(a.answer);
    const isCorrect = correctList.some((correct) => getSimilarity(normalized, correct) >= 0.8);
    if (a.awarded) return;

    let pts = 0;
    if (isCorrect && startTs) {
      const elapsed = elapsedSecondsBetween(startTs, a.submittedAt);
      const elapsedSeconds = elapsed == null
        ? 0
        : Math.max(0, Math.min(totalSeconds, elapsed));
      pts = Math.round(maxPoints * ((totalSeconds - elapsedSeconds) / totalSeconds));
    }

    batch.set(
      doc(db, "games", gameCode, "answers", `${current}-${a.team}`),
      {
        ...a,
        points: typeof pts === "number" ? pts : 0,
        awarded: true,
        correct: isCorrect,
        q: current,
        team: a.team,
        answer: a.answer,
      },
      { merge: true }
    );
  });

  await batch.commit();
}

// Quizmaster.js — BREADCRUMBS: score by first correct attempt and clue index
async function scoreBreadcrumbsQuestion(gameCode, current, q) {
  const answersSnap = await getDocs(collection(db, "games", gameCode, "answers"));
  const facts = Array.isArray(q.facts) ? q.facts : [];
  const numClues = Math.max(1, facts.length);
  const totalSeconds = (q && typeof q.time === "number" && q.time > 0) ? q.time : 20;
  const maxPoints = typeof q.points === "number" && !isNaN(q.points) ? q.points : Number(q.points) || 20;
  const correctList = (q.answers || []).filter(Boolean).map((t) => (String(t || "").replace(/[^A-Z0-9]/gi, "").toUpperCase()));

  const gameDocSnap = await getDoc(doc(db, "games", gameCode));
  const gameData = gameDocSnap.data() || {};
  const startTs = gameData.questionStartTimeServer || gameData.questionStartTime || gameData.questionStartTimeLegacyMs;

  const batch = writeBatch(db);

  answersSnap.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.q !== current || !data.team) return;
    if (data.awarded) return;

    let attempts = Array.isArray(data.attempts) ? data.attempts : [];
    if (attempts.length === 0 && data.answer != null) {
      const singleSubmittedAt = data.submittedAt ?? data.time;
      if (singleSubmittedAt != null) {
        attempts = [{ answer: data.answer, submittedAt: singleSubmittedAt }];
      }
    }
    if (attempts.length === 0) return;

    const sortedAttempts = [...attempts].sort((a, b) => {
      const aMs = a.submittedAt && typeof a.submittedAt.toMillis === "function" ? a.submittedAt.toMillis() : (typeof a.submittedAt === "number" ? a.submittedAt : Infinity);
      const bMs = b.submittedAt && typeof b.submittedAt.toMillis === "function" ? b.submittedAt.toMillis() : (typeof b.submittedAt === "number" ? b.submittedAt : Infinity);
      return aMs - bMs;
    });

    let firstCorrectAttempt = null;
    let clueIndex = numClues - 1;
    for (const att of sortedAttempts) {
      const normalized = (att.answer || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
      const isCorrect = correctList.length > 0 && correctList.some((c) => getSimilarity(normalized, c) >= 0.8);
      if (isCorrect) {
        firstCorrectAttempt = att;
        const elapsed = startTs ? elapsedSecondsBetween(startTs, att.submittedAt) : null;
        const elapsedSec = elapsed == null ? totalSeconds : Math.max(0, Math.min(totalSeconds, elapsed));
        if (facts.length > 0 && totalSeconds > 0) {
          clueIndex = Math.min(facts.length - 1, Math.floor((elapsedSec / totalSeconds) * facts.length));
        } else {
          clueIndex = 0;
        }
        break;
      }
    }

    let points = 0;
    let isCorrect = false;
    let winningAnswer = null;
    if (firstCorrectAttempt) {
      isCorrect = true;
      winningAnswer = firstCorrectAttempt.answer;
      points = Math.round(maxPoints * (numClues - clueIndex) / numClues);
      points = Math.max(0, Math.min(maxPoints, points));
    }

    // Firestore rejects undefined values, and one bad row fails the whole batch (no team gets points),
    // so only include answer / correctAtClueIndex when they have a value.
    const scored = { ...data, points, correct: isCorrect, awarded: true };
    const answerValue = winningAnswer != null ? winningAnswer : data.answer;
    if (answerValue !== undefined) scored.answer = answerValue;
    if (firstCorrectAttempt != null) scored.correctAtClueIndex = clueIndex;
    batch.set(doc(db, "games", gameCode, "answers", `${current}-${data.team}`), scored, { merge: true });
  });

  await batch.commit();
}

// Quizmaster.js — NEW
async function scoreOutOfOrder(gameCode, current, q) {
  if (!gameCode || !q) return;

  // Pull all answers for this question
  const answersSnap = await getDocs(collection(db, "games", gameCode, "answers"));

  // Points PER CORRECT POSITION (same policy as MULTIANSWER)
  const perCorrect =
    typeof q.points === "number" && !isNaN(q.points) ? q.points : Number(q.points) || 0;

  // Correct order is by ORIGINAL index (0..n-1)
  const n = Array.isArray(q.answers) ? q.answers.length : 0;
  const correctAt = (pos, answerIdx) => answerIdx === pos;

  const batch = writeBatch(db);

  answersSnap.forEach((docSnap) => {
    const d = docSnap.data();
    if (d.q !== current || !Array.isArray(d.answer) || !d.team) return;

    const order = d.answer; // array of original answerIdx in the player’s final order
    const len = Math.min(n, order.length);

    let correctCount = 0;
    for (let i = 0; i < len; i++) {
      if (correctAt(i, order[i])) correctCount++;
    }

    const points = correctCount * perCorrect;
    const isCorrect = correctCount > 0; // match MULTIANSWER semantics

    batch.set(
      doc(db, "games", gameCode, "answers", `${current}-${d.team}`),
      {
        points,
        awarded: true,
        correct: isCorrect,
        q: current,
        team: d.team,
        answer: order, // keep their submitted array
      },
      { merge: true }
    );
  });

  await batch.commit();
}


// Quizmaster.js — REPLACE the whole function
async function scoreVanishingChoice(gameCode, current, correctIdx, q) {
  if (!gameCode) return;

  const answersSnap = await getDocs(collection(db, "games", gameCode, "answers"));
  const teamAnswers = [];
  answersSnap.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.q === current && data.answer !== undefined && data.answer !== null) {
      teamAnswers.push({
        team: data.team,
        answer: Number(data.answer),
        submittedAt: data.submittedAt || data.time || null, // server or legacy
        visibleOptionsAtAnswer: data.visibleOptionsAtAnswer, // already saved by player
        docId: docSnap.id,
      });
    }
  });

  const gameDocSnap = await getDoc(doc(db, "games", gameCode));
  const gameData = gameDocSnap.data() || {};
  const startTs = gameData.questionStartTimeServer || gameData.questionStartTime || gameData.questionStartTimeLegacyMs;

  const totalSeconds = (q && typeof q.time === "number" && q.time > 0) ? q.time : 20;
  const MIN_PERCENT = 0.1;

  const batch = writeBatch(db);

  teamAnswers.forEach((a) => {
    // Elapsed from server stamps
    const elapsed = elapsedSecondsBetween(startTs, a.submittedAt);
    const elapsedSeconds = elapsed == null
      ? 0
      : Math.max(0, Math.min(totalSeconds, elapsed));

    const scale = MIN_PERCENT + (1 - MIN_PERCENT) * ((totalSeconds - elapsedSeconds) / totalSeconds);
    const scaledPoints = Math.round((q.points || 0) * scale);

    const isCorrect = a.answer === correctIdx;
    const points = isCorrect ? scaledPoints : -scaledPoints;

    batch.set(
      doc(db, "games", gameCode, "answers", `${current}-${a.team}`),
      {
        points,
        awarded: true,
        correct: isCorrect,
        q: current,
        team: a.team,
        answer: a.answer,
        // keep original fields for continuity
        submittedAt: a.submittedAt,
        visibleOptionsAtAnswer: a.visibleOptionsAtAnswer,
      },
      { merge: true }
    );
  });

  await batch.commit();
}





// Quizmaster.js (near your imports or top of file)
function getShuffledOrder(length) {
  const arr = Array.from({ length }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function getRandomVanishOrder(answersOrder) {
  // Remove the correct answer (index 0)
  const incorrectAnswerIndexes = answersOrder.filter((idx) => idx !== 0);
  // Shuffle
  for (let i = incorrectAnswerIndexes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [incorrectAnswerIndexes[i], incorrectAnswerIndexes[j]] = [
      incorrectAnswerIndexes[j],
      incorrectAnswerIndexes[i],
    ];
  }
  return incorrectAnswerIndexes;
}

const VANISH_POOF_MS = 650;

async function resolveMediaUrls(questions, onProgress) {
  // Prefer *_URL when present; otherwise resolve filename via Storage
  const pairs = [
    ["SOUND", "SOUND_URL"],
    ["VIDEO", "VIDEO_URL"],
    ["BACKGROUND", "BACKGROUND_URL"],
    ["P1", "P1_URL"]
  ];
  
  // SPOTIFY_URL is handled separately (no filename resolution needed)

  const out = [];
  const total = questions.length;
  const batchSize = 8;
  let completed = 0;

  for (let i = 0; i < total; i += batchSize) {
    const chunk = questions.slice(i, i + batchSize);
    const promises = chunk.map(async (q) => {
      const qCopy = { ...q };

      for (const [field, urlField] of pairs) {
        const lower = field.toLowerCase();

        // If a URL column exists, prefer it
        const urlCandidate = qCopy[urlField];
        if (urlCandidate && String(urlCandidate).startsWith("https://")) {
          qCopy[field] = urlCandidate;
          qCopy[lower] = urlCandidate;
          continue;
        }

        // Else try resolving the filename field (upper or lower case key)
        const val = qCopy[field] || qCopy[lower];
        if (val && !String(val).toLowerCase().startsWith("http")) {
          const candidate = cleanFilename(val);
          if (candidate) {
            try {
              const fileRef = ref(storage, "media/" + candidate);
              const realUrl = await getDownloadURL(fileRef);
              qCopy[field] = realUrl;
              qCopy[lower] = realUrl;
            } catch {
              // keep as is if not found
            }
          }
        }
      }

      // Normalise final field names used by the host UI
      // Priority for sound: SOUND (filename) > SOUND_URL (MP3 URL) > SPOTIFY_URL (Spotify embed URL)
      return {
        ...qCopy,
        sound: qCopy.SOUND || qCopy.SOUND_URL || qCopy.SPOTIFY_URL || qCopy.sound || null,
        video: qCopy.VIDEO_URL || qCopy.VIDEO || qCopy.video || null,
        background: qCopy.BACKGROUND_URL || qCopy.BACKGROUND || qCopy.background || null,
        p1: qCopy.P1_URL || qCopy.P1 || qCopy.p1 || null,
      };
    });

    const chunkResults = await Promise.all(promises);
    out.push(...chunkResults);
    completed += chunk.length;
    if (onProgress) onProgress(completed / total);
  }
  return out;
}


async function handleSelect(e) {
  const idx = e.target.value;
  if (!quizzes[idx]) return;
  const fileRef = quizzes[idx];
  const url = await getDownloadURL(fileRef);
  const res = await fetch(url);
  const csv = await res.text();
  Papa.parse(csv, {
    header: true,
    skipEmptyLines: true,
    complete: async (results) => {
      // Transform questions the same way handleCSV does
      let qs = results.data.map((q) => {
        const filled = {};
        for (const field of REQUIRED_FIELDS) filled[field] = q[field] ?? "";

        const catUpper = String(filled.CAT || q.CAT || filled.type || q.type || "").trim().toUpperCase();
        const isBreadcrumbs = catUpper === "BREADCRUMBS";
        const rawAnswers = isBreadcrumbs
          ? [q.A1].filter((a) => !!a)
          : [filled.A1, filled.A2, filled.A3, filled.A4, filled.A5, filled.A6].filter((a) => !!a);
        const facts = isBreadcrumbs ? [q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11].filter((a) => !!a) : undefined;

        let maxSelectable = 1;
        if (catUpper === "MULTIANSWER") {
          maxSelectable = rawAnswers.filter((a) => /^\s*\[.*?\]\s*$/.test(a)).length;
        }

        const answers = rawAnswers;
        const answersOrder = getShuffledOrder(answers.length);
        const rawTime = typeof filled.TIME !== "undefined" && filled.TIME !== ""
          ? Number(filled.TIME) : undefined;

        const time = !isNaN(rawTime) && rawTime !== null
          ? rawTime
          : settings.questionTimer || 20;

        return {
          ...q,  // Spread all CSV fields first to preserve all columns
          question: filled.Q,
          Q: filled.Q,
          answers,
          ...(isBreadcrumbs ? { facts: facts || [] } : {}),
          type: filled.CAT,
          CAT: filled.CAT,
          sound: filled.SOUND,
          video: filled.VIDEO,
          background: filled.BACKGROUND,
          p1: filled.P1,
          answersOrder,
          maxSelectable,
          vanishOrder:
            catUpper === "VANISHINGCHOICE"
              ? getRandomVanishOrder(answersOrder)
              : undefined,
          time,
          TIME: time,
          visibleExplanation: q.visibleExplanation || "",  // Explicitly preserve visibleExplanation from CSV
          JIGSAW: q.JIGSAW || "",
        };
      });

      qs = qs.filter(q => !!q.question);
      
      // Resolve all media filenames to URLs!
      const fixed = await resolveMediaUrls(qs);
      onQuizLoaded(fixed);
    },
  });
}

// === Scores gate: +3s delay before showing, then 15s on-screen ===
// === Scores gate: +3s delay on underlying slide, then show scores for 15s ===
// IMPORTANT: This version *locks* auto-advance while active so the slide
// can't advance underneath the 3s delay or the 15s scores window.
// === Scores gate: +3s dwell on underlying slide, while preventing early auto-advance ===
function QuestionScoresGate({ show, idx, gameCode, onCloseAndAdvance }) {
  if (!show) {
    return null;
  }

  return (
    <QuestionScores
      questionIdx={idx}
      gameCode={gameCode}
      // The scores component will call this when its internal timer finishes.
      onClose={() => {
        try {
          onCloseAndAdvance();
        } catch {}
      }}
    />
  );
}



function findRoundStart(questions, currentQuestionIndex) {
  if (!Array.isArray(questions) || currentQuestionIndex <= 0) {
    return 0;
  }
  // Search backward from the current question index
  for (let i = currentQuestionIndex; i >= 0; i--) {
    const q = questions[i];
    const type = String(q?.type || q?.CAT || "").toUpperCase();
    
    // The round starts at the question *after* the STARTROUND slide, or at index 0.
    // However, the Score component will need the index of the *first question* in the round.
    // If the score display is triggered by ENDROUND, we check one index back.
    if (type === "STARTROUND" || type === "JOIN QUIZ" || type === "INTRODUCTION") {
        // If we hit a STARTROUND or the start of the quiz, the round started at index i + 1.
        return i + 1;
    }
  }
  // Default to the start of the quiz
  return 0;
}


export default function Quizmaster() {

  const [profileName, setProfileName] = useState("");
   const testUsersAddedRef = useRef(false);
   const billingMaxQRef = useRef(0);
   const liveTokenSpentRef = useRef(false); 
  // ---- State ----
  // Debounce handle for speaking so fast-skips don't trigger TTS

  // Add near other useState hooks
  const [showMenu, setShowMenu] = React.useState(false);

  const speakDelayRef = React.useRef(0);

  const [currentCsvName, setCurrentCsvName] = useState(null);

  const [joinCode, setJoinCode] = useState("");

  const [gameCode, setGameCode] = useState(null);
  const [step, setStep] = useState(1); // 1 = pick quiz, 2 = upload media, 3 = quiz controls (0 = options, shown only when hosting)

  const [questions, setQuestions] = useState([]);
  const [mediaNeeded, setMediaNeeded] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [current, setCurrent] = useState(0);
  const [tab, setTab] = useState("questions");
  const [progress, setProgress] = useState(0);
  const [missingFiles, setMissingFiles] = useState([]);

  const imageFilename = "90s v2.d0df8704-fdfe-4b08-a534-a7ff5c5ad17f.png";
  const [hardcodedUrl, setHardcodedUrl] = useState("");

  const [settings, setSettings] = useState({ questionTimer: 5 }); // or your preferred default
  const [timer, setTimer] = useState(settings.questionTimer); // start timer from settings
  const [timerActive, setTimerActive] = useState(false); // add this!

  const [showTopBar, setShowTopBar] = useState(true);
  const [showQuizOptions, setShowQuizOptions] = useState(true);
  const [bgUrl, setBgUrl] = useState("");
  const [selectedAnswers, setSelectedAnswers] = useState([]);

  const NEAREST_POINTS = [20, 15, 10, 5, 2];
  const [allAnswers, setAllAnswers] = useState([]);

  const [vanishingIndexes, setVanishingIndexes] = useState([]); // Indexes of currently visible answers
  const [vanishingPlan, setVanishingPlan] = useState(null);
  const [vanishIntervalId, setVanishIntervalId] = useState(null);

  const [justVanished, setJustVanished] = useState([]); // holds answerIdxs currently animating out
  const [isHost, setIsHost] = useState(true); // set manually for now
  const pingRef = useRef();
  const [showTopTeams, setShowTopTeams] = useState(false);
  const [topTeams, setTopTeams] = useState([]); // Will hold top teams data for the popup
  const sortedTeams = [...topTeams].sort((a, b) => a.points - b.points);
  const doubledTopTeams = [...sortedTeams, ...sortedTeams];
  const scrollRef = useRef(null);
  const [rowWidth, setRowWidth] = useState(0);


const [teamInfo, setTeamInfo] = useState({});
const [selfiesReady, setSelfiesReady] = useState(false);
const [hasEnteredQuiz, setHasEnteredQuiz] = useState(false);


const [scrollReady, setScrollReady] = useState(false);
const [revealedIndexes, setRevealedIndexes] = useState([]); // for "WHEEL" round
const [showQuestionScores, setShowQuestionScores] = useState(false);
const [lastQuestionIdx, setLastQuestionIdx] = useState(null);
//const [showTeamsIntro, setShowTeamsIntro] = useState(false);

const [resumeQuestionIdx, setResumeQuestionIdx] = useState(null);

const scoredRef = useRef({});
const questionsRef = useRef(questions);
const vanishStartedForRef = useRef(null);
const vanishTimerIdsRef = useRef([]);
const questionStartTimeRef = useRef(0);
const timerRef = useRef(0);

/** Firestore + local timer fields when advancing to a new question. */
const applyAdvanceTimerFields = (q, seconds, payload) => {
  const hasTimer = !!seconds;
  const active = timerActiveOnQuestionAdvance(q, hasTimer);
  payload.timer = seconds;
  payload.timerActive = active;
  payload.vanishingPlan = null;
  if (active) {
    const t = Date.now();
    questionStartTimeRef.current = t;
    payload.questionStartTime = t;
    payload.questionStartTimeServer = serverTimestamp();
  }
  return active;
};

questionsRef.current = questions;
const [user, setUser] = useState(null);
const [checking, setChecking] = useState(true);
const [quizFiles, setQuizFiles] = useState([]);
const [loadingQuizzes, setLoadingQuizzes] = useState(true);


const [editMode, setEditMode] = useState(false);
const [editedQuestions, setEditedQuestions] = useState([]);
const [editedQuizFile, setEditedQuizFile] = useState(null);
const [editCurrent, setEditCurrent] = useState(0); // current question being edited
const [selectedQuiz, setSelectedQuiz] = useState(null);
const [uploading, setUploading] = useState(false);
const [hosting, setHosting] = useState(false);
const [hostProgress, setHostProgress] = useState(0);

const [showReconnect, setShowReconnect] = useState(false);
const [activeGames, setActiveGames] = useState([]);
const [loadingGames, setLoadingGames] = useState(false);
const [reconnectError, setReconnectError] = useState("");
const [appendingQuestions, setAppendingQuestions] = useState(false);
const [userProfile, setUserProfile] = useState(null);

const [isUploadingLibrary, setIsUploadingLibrary] = useState(false);
const [libraryUploadProgress, setLibraryUploadProgress] = useState("");

const [roundQuestionStartIdx, setRoundQuestionStartIdx] = useState(0);
const [teamTagPreflight, setTeamTagPreflight] = useState(null);
const [csvTeamChoiceOffWarn, setCsvTeamChoiceOffWarn] = useState(false);
const isAdvancingRef = useRef(false);
// Test Mode limit message is shown once per hosted game; later auto-advance attempts stop silently.
const testLimitAlertShownRef = useRef(false);
const lastSeenQuestionVersionRef = useRef(0);
const [selectionStatus, setSelectionStatus] = useState("idle");
const selectingOverlayRef = useRef(null);

const getConfiguredTestUserCount = () => {
  const raw = localStorage.getItem("opt_test_user_count");
  let count = parseInt(raw ?? "0", 10);
  if (Number.isNaN(count)) count = 0;
  count = Math.max(0, Math.min(testTeams.length, count));
  if (count === 0) {
    const legacyOn = (localStorage.getItem("opt_add_test_users") || "Off") === "On";
    if (legacyOn) {
      count = Math.min(testTeams.length, 5);
    }
  }
  return count;
};

// Quizmaster.js

// Make the advance function async to handle Firestore lookups
// Quizmaster.js - Modified advance function (removes used question check)

// Quizmaster.js - REPLACE the entire advance function with this

const advance = async (by, manual = false) => {
  console.log("--- ADVANCE FUNCTION CALLED (Iterative Fetch Version) ---"); // Diagnostic log
  if (manual) {
    try { window.__skipWheelTransitionOnce = true; } catch {}
  }

  // --- Existing initial logic (preserved) ---
  if (isAdvancingRef.current) {
    console.warn("advance blocked: already in progress");
    return;
  }
  isAdvancingRef.current = true;
  try {
  setShowAnswerReveal(false);
  if (current === 0 && by > 0 && !testUsersAddedRef.current) {
    testUsersAddedRef.current = true;
    const configuredTestUsers = getConfiguredTestUserCount();
    if (configuredTestUsers > 0) {
      const batch = writeBatch(db);
      testTeams.slice(0, configuredTestUsers).forEach(user => {
        const teamDocRef = doc(db, "games", gameCode, "teams", user.name);
        batch.set(teamDocRef, {
          team: user.name,
          selfieUrl: user.selfie,
          joinedAt: Date.now() - Math.floor(Math.random() * 30000)
        });
      });
      await batch.commit();
    }
  }
  setShowTopTeams(false);
  window.dispatchEvent(new CustomEvent("quiz:advance"));
  if (manual) {
    setAutoPaused(true);
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = 0; }
  }
  if (questions[current] && (questions[current].type || "").toUpperCase() === "END QUIZ" && by > 0) {
    setTab("scores");
    isAdvancingRef.current = false;
    return;
  }
  // --- End Existing initial logic ---

  const targetIdx = Math.min(Math.max(current + by, 0), questions.length - 1);

  // Test Mode: free practice capped at FREE_TEST_QUESTION_LIMIT *playable* questions
  // (JOIN/INTRODUCTION/SHOWROUNDS/STARTROUND/etc. do not count).
  if (
    by > 0 &&
    isTestHostMode() &&
    wouldExceedFreeTestLimit(questions, targetIdx, FREE_TEST_QUESTION_LIMIT)
  ) {
    setAutoPaused(true);
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = 0;
    }
    if (testLimitAlertShownRef.current) {
      isAdvancingRef.current = false;
      return;
    }
    testLimitAlertShownRef.current = true;
    appAlert(
      `You've reached the free Test Mode limit of ${FREE_TEST_QUESTION_LIMIT} quiz questions.\n\n` +
      "To run a full live quiz, open Quiz Options (O), set Test Mode to Off, and host again.\n\n" +
      "Hosting live spends 1 quiz token."
    );
    isAdvancingRef.current = false;
    return;
  }

  // --- Placeholder Detection & Replacement Logic ---
  let qToAdvanceTo = questions[targetIdx] || {};
  const originalQField = qToAdvanceTo.Q || qToAdvanceTo.question || "";
  let finalQuestionObject = qToAdvanceTo;
  const rowCatUpper = String(qToAdvanceTo.CAT || qToAdvanceTo.type || "").trim().toUpperCase();
  let isPlaceholder =
    typeof originalQField === "string" &&
    originalQField.startsWith("#") &&
    !NO_LIBRARY_PLACEHOLDER_CATS.has(rowCatUpper);

  console.log(
    `ADVANCE CHECK @ index ${targetIdx}: isPlaceholder=${isPlaceholder}, CAT=${rowCatUpper}, Q field is: "${originalQField}"`
  );

  if (isPlaceholder && by > 0) {
    const hashtags = extractHashtagsFromText(originalQField);
    const placeholderTag = hashtags[0] || null;
    const requestedTagsRaw = hashtags.slice(1);
    let requestedTagsNormalized = requestedTagsRaw
      .map(normalizeTagValue)
      .filter(Boolean);
    const placeholderCat = (qToAdvanceTo.CAT || qToAdvanceTo.type || "").toUpperCase();

    if (placeholderTag && placeholderCat) {
      try {
        const sessSnap = await getDoc(doc(db, "games", gameCode, "roundTagChoice", "session"));
        const extraT = getExtraTeamChoiceTagForPlaceholder(
          sessSnap.data(),
          questionsRef.current,
          targetIdx
        );
        if (extraT) {
          const nt = normalizeTagValue(extraT);
          if (nt && !requestedTagsNormalized.includes(nt)) {
            requestedTagsNormalized = [...requestedTagsNormalized, nt];
          }
        }
      } catch (e) {
        console.warn("Team choice extra tag:", e);
      }
      const tagSuffix = requestedTagsRaw.length > 0
        ? ` ${requestedTagsRaw.map(tag => `#${tag}`).join(" ")}`
        : "";
      console.log(`Placeholder detected: #${placeholderTag}${tagSuffix}, Category: ${placeholderCat}. Attempting replacement...`);
      try {
        // Mark selection start on the game doc to avoid concurrent selection
        try {
          const gameDocRef = doc(db, "games", gameCode);
          const snap = await getDoc(gameDocRef);
          const g = snap.data() || {};
          if (g.selectionStatus === "selecting") {
            const startedAt = Number(g.selectionStartedAt) || 0;
            const staleMs = Date.now() - startedAt;
            // Clear stuck "selecting" locks (e.g. after a failed advance) so hosts can move on.
            if (!startedAt || staleMs > 8000) {
              console.warn("Clearing stale selectionStatus=selecting before advance.");
              await updateDoc(gameDocRef, {
                selectionStatus: "ready",
                selectionToken: "",
              });
            } else {
              console.warn("Selection already in progress on server; aborting duplicate advance.");
              return;
            }
          }
          // Reset the timer with the index: a leftover 0 from the previous question would make
          // phones auto-submit (and lock) the moment they see the new slide.
          await updateDoc(gameDocRef, {
            selectionStatus: "selecting",
            selectionToken: Math.random().toString(36).slice(2),
            selectionStartedAt: Date.now(),
            current: targetIdx,
            timer: Number(qToAdvanceTo.TIME ?? qToAdvanceTo.time) || 0,
            timerActive: false,
            answersGateQ: null,
          });
        } catch (markErr) {
          console.warn("Could not mark selection start:", markErr);
        }
        // --- Retrieve Current Venue & Time ---
        const currentVenueRaw = localStorage.getItem("opt_venue") || "Unknown Venue";
        const currentVenue = String(currentVenueRaw).trim() || "Unknown Venue";
        const isTestingVenue = currentVenue === "Test Venue";
        // --- End Retrieve ---

        // --- Library questions already used in this game (never twice in one game, Test Mode or not) ---
        const isTestMode = localStorage.getItem("opt_test_mode") === "On";
        let usedInThisGame = new Set();
        try {
          const gameSnap = await getDoc(doc(db, "games", gameCode));
          usedInThisGame = libraryIdsUsedInGame(gameSnap.data(), targetIdx);
        } catch (err) {
          console.warn("Failed to read library questions used in this game:", err);
        }
        // --- End Read Used Question IDs ---

        // --- Iterative Fetching Logic ---
        const BATCH_SIZE = 20; // How many candidates to fetch per batch
        const MAX_BATCHES = 5; // Max number of batches to try (total 100 candidates)
        let lastVisibleDoc = null; // For pagination cursor
        let foundQuestionData = null;
        let foundQuestionId = null;

        const fetchTagSuffix = requestedTagsRaw.length > 0
          ? ` ${requestedTagsRaw.map(tag => `#${tag}`).join(" ")}`
          : "";
        console.log(`Starting iterative fetch for #${placeholderTag}${fetchTagSuffix} / ${placeholderCat} at venue ${currentVenue}`);

        for (let batchNum = 0; batchNum < MAX_BATCHES; batchNum++) {
          console.log(`Fetching batch ${batchNum + 1}...`);
          // Base query: include accuracyFlag so we fetch same pool as CSV editor (top 100 UNFLAGGED)
          let libraryQuery = query(
            collection(db, "QuestionLibrary"),
            where("genre", "==", placeholderTag),
            where("questionType", "==", placeholderCat),
            where("accuracyFlag", "==", ""),
            orderBy("interestRating", "desc"),
            limit(BATCH_SIZE)
          );

          // Add cursor for subsequent batches
          if (lastVisibleDoc) {
            libraryQuery = query(libraryQuery, startAfter(lastVisibleDoc));
          }

          const librarySnap = await getDocs(libraryQuery);
          const docsInBatch = librarySnap.docs;
          console.log(`Batch ${batchNum + 1} returned ${docsInBatch.length} candidates.`);

          if (docsInBatch.length === 0) {
            console.log("No more candidates found in Firestore.");
            break; // No more documents match the base query
          }

          // Update cursor for the *next* iteration
          lastVisibleDoc = docsInBatch[docsInBatch.length - 1];

          // Filter this batch locally
          for (const docSnap of docsInBatch) {
            const candidateData = docSnap.data();
            const candidateId = docSnap.id;
            const usageHistory = candidateData.usageHistory || [];

            // Filter out questions with accuracy flags (local filtering)
            const accuracyFlag = candidateData.accuracyFlag || "";
            if (accuracyFlag.trim() !== "") {
              console.log(`Skipping question ${candidateId}: has accuracy flag "${accuracyFlag}"`);
              continue;
            }

            if (requestedTagsNormalized.length > 0) {
              const candidateTags = normalizeTagList(candidateData.tags);
              const tagMatches = requestedTagsNormalized.every((requested) =>
                candidateTags.includes(requested)
              );
              if (!tagMatches) {
                console.log(`Skipping question ${candidateId}: missing one or more required tags (${requestedTagsRaw.map(tag => `#${tag}`).join(" ")})`);
                continue;
              }
            }

            let isSuitable = false;

            // Never the same library question twice in one game
            if (usedInThisGame.has(candidateId)) {
              console.log(`Skipping question ${candidateId}: already used in this game`);
              continue; // Skip this question
            }

            // JACKPOT: must never have been used at ANY venue (usageHistory must be empty)
            if (placeholderCat === "JACKPOT") {
              isSuitable = usageHistory.length === 0;
              if (!isSuitable) {
                console.log(`Skipping JACKPOT question ${candidateId}: already used (usageHistory.length=${usageHistory.length})`);
              }
            } else if (!isTestingVenue) {
              // Other venues: block questions played at this venue inside the host's
              // "don't repeat" window (Account → Your venues; forever by default)
              const previouslyUsedAtVenue = usedAtVenueRecently(usageHistory, currentVenue);

              isSuitable = !previouslyUsedAtVenue;
              if (!isSuitable) {
                console.log(`Skipping question ${candidateId}: has history at venue ${currentVenue}`);
              }
            } else {
              // Test Venue: bypass venue-specific reuse filtering entirely
              isSuitable = true;
            }

            if (isSuitable) {
              // This question is suitable, select it and stop searching
              foundQuestionData = { id: candidateId, ...candidateData };
              foundQuestionId = candidateId;
              console.log(`Selected unused question: ${foundQuestionId} from batch ${batchNum + 1}`);
              break; // Exit the inner loop (filtering this batch)
            }
          } // End filtering loop for this batch

          // If we found a question in this batch, exit the outer loop too
          if (foundQuestionData) {
            break;
          }

          // If Firestore returned fewer docs than requested, there are no more to fetch
          if (docsInBatch.length < BATCH_SIZE) {
             console.log("Last batch fetched, no more candidates.");
             break;
          }

        } // End outer loop (fetching batches)
        // --- End Iterative Fetching Logic ---

        if (foundQuestionData) {
          // *** Merge logic (preserved) ***
          const libraryQ = foundQuestionData;
          const placeholderQ = qToAdvanceTo;
          const libraryCat = (libraryQ.questionType || "").toUpperCase();
          const isBreadcrumbs = libraryCat === "BREADCRUMBS";
          const libraryAnswersRaw = isBreadcrumbs
            ? [libraryQ.answer].filter(a => a != null && String(a).trim() !== "")
            : [ libraryQ.answer, libraryQ.A2, libraryQ.A3, libraryQ.A4, libraryQ.A5, libraryQ.A6 ].filter(a => a != null && String(a).trim() !== "");
          const libraryFacts = isBreadcrumbs ? [libraryQ.A2, libraryQ.A3, libraryQ.A4, libraryQ.A5, libraryQ.A6, libraryQ.A7, libraryQ.A8, libraryQ.A9, libraryQ.A10, libraryQ.A11].filter(a => a != null && String(a).trim() !== "") : undefined;
          let maxSelectable = 1;
          const liveAnswers = libraryAnswersRaw;
          if (libraryCat === "MULTIANSWER") { maxSelectable = libraryAnswersRaw.filter(a => /^\s*\[.*?\]\s*$/.test(String(a))).length; }


          // --- Calculate merged time/points first ---
          const mergedTime = placeholderQ.TIME || placeholderQ.time || settings.questionTimer || 20;
          // JACKPOT: always 0 points
          const mergedPoints = placeholderCat === "JACKPOT" ? 0 : (placeholderQ.POINTS || placeholderQ.points || 0);

          // Calculate answersOrder once and reuse it for vanishOrder
          const calculatedAnswersOrder = getShuffledOrder(liveAnswers.length);

            finalQuestionObject = {
            // Keep from Placeholder (CSV data)
            TIME: mergedTime,       // Uppercase
            time: mergedTime,       // <<<--- ADD lowercase 'time'
            POINTS: String(mergedPoints),   // Uppercase (always "0" for JACKPOT)
            points: mergedPoints,   // <<<--- ADD lowercase 'points' (always 0 for JACKPOT)
            BACKGROUND_URL: placeholderQ.BACKGROUND_URL || placeholderQ.background || "",

            // Take from Library Question (Database data)
            question: libraryQ.question || "Error: Question text missing",
            Q: libraryQ.question || "Error: Question text missing",
            CAT: libraryCat, type: libraryCat,
            A1: libraryQ.answer || null, A2: libraryQ.A2 || null, A3: libraryQ.A3 || null, A4: libraryQ.A4 || null, A5: libraryQ.A5 || null, A6: libraryQ.A6 || null,
            A7: libraryQ.A7 || null, A8: libraryQ.A8 || null, A9: libraryQ.A9 || null, A10: libraryQ.A10 || null, A11: libraryQ.A11 || null,
            NOTES: libraryQ.notes || "", notes: libraryQ.notes || "",
            P1_URL: libraryQ.Medialink || "", p1: libraryQ.Medialink || "",
            SOUND_URL: libraryQ.Audiolink || "", // <<<--- MAP Audiolink HERE
            sound: libraryQ.Audiolink || "",     // <<<--- MAP Audiolink HERE too (lowercase alias)
            visibleExplanation: libraryQ.visibleExplanation || "", // <<<--- ADD visibleExplanation field
            // The quiz row decides; a library question can also ask for it with a `jigsaw` field.
            JIGSAW: String(placeholderQ.JIGSAW ?? "").trim() !== "" ? String(placeholderQ.JIGSAW) : String(libraryQ.jigsaw ?? ""),

            // Derived fields
            answers: liveAnswers,
            ...(isBreadcrumbs ? { facts: libraryFacts || [] } : {}),
            answersOrder: calculatedAnswersOrder,
            maxSelectable: maxSelectable,
            vanishOrder: libraryCat === "VANISHINGCHOICE" ? getRandomVanishOrder(calculatedAnswersOrder) : null,
            _placeholderUsed: originalQField, _libraryQuestionId: foundQuestionId,
          };
          // --- End Merge ---






          // JACKPOT: Store locally and set paused, don't write to game doc until unpaused
          if (placeholderCat === "JACKPOT") {
            pendingJackpotRef.current = {
              questionData: finalQuestionObject,
              targetIdx: targetIdx,
              questionId: foundQuestionId,
              currentVenue: currentVenue
            };
            setAutoPaused(true);
            setOptAutoplayLocal(false);
            // Clear selection status but don't write question to game doc
            try {
              const gameDocRef = doc(db, "games", gameCode);
              await updateDoc(gameDocRef, {
                selectionStatus: "ready",
                selectionToken: "",
                autoPaused: true,
                opt_autoplay: false
              });
            } catch {}
            console.log("JACKPOT question loaded and stored locally. Waiting for unpause.");
            // Set local state so UI shows we're on this question (even though it's paused)
            setCurrent(targetIdx);
            setTimer(finalQuestionObject.TIME || finalQuestionObject.time || 0);
            setTimerActive(!!(finalQuestionObject.TIME || finalQuestionObject.time));
            isAdvancingRef.current = false;
            return; // Exit early, don't proceed with normal advance
          } else {
            // Finalize selection to game doc in a single write (non-JACKPOT questions)
            try {
              const gameDocRef = doc(db, "games", gameCode);
              const snap = await getDoc(gameDocRef);
              const g = snap.data() || {};
              const nextVersion = (g.questionVersion || 0) + 1;
              // Build updated questions array to keep full history
              let questionsArray = [];
              if (Array.isArray(g.questions)) {
                questionsArray = [...g.questions];
              } else if (g.questions) {
                try { questionsArray = Object.values(g.questions); } catch { questionsArray = []; }
              }
              while (questionsArray.length <= targetIdx) questionsArray.push({});
              questionsArray[targetIdx] = finalQuestionObject;
              const qSec = finalQuestionObject.TIME || finalQuestionObject.time || 0;
              const baseUpdatePayload = { current: targetIdx };
              const timerActiveNow = applyAdvanceTimerFields(
                finalQuestionObject,
                qSec,
                baseUpdatePayload
              );
              // Do not set answersGateQ here — Quizscreen opens the gate after host hero/TTS (same as normal advance).
              const updatePayload = {
                ...baseUpdatePayload,
                selectionStatus: "ready",
                selectionToken: "",
                questionVersion: nextVersion,
                currentQuestion: finalQuestionObject,
                questions: questionsArray,
              };

              // In test mode, add the selected question ID to testModeUsedQuestionIds
              if (isTestMode && foundQuestionId) {
                updatePayload.testModeUsedQuestionIds = arrayUnion(foundQuestionId);
              }

              await updateDoc(gameDocRef, updatePayload);

              // Only set current state AFTER DB write completes - prevents rendering stale question data
              setCurrent(targetIdx);
              setTimer(qSec);
              setTimerActive(timerActiveNow);
              try { scoredRef.current[targetIdx] = false; } catch {}

              // Record usage after final write
              try {
                const isTestMode = localStorage.getItem("opt_test_mode") === "On";
                if (!isTestingVenue && !isTestMode) {
                  const usageEntry = {
                    usedAt: new Date(),
                    gameId: gameCode || "unknown_game",
                    hostId: auth.currentUser?.uid || "unknown_host",
                    location: { city: "Brisbane", state: "QLD", country: "AU", venueId: currentVenue }
                  };
                  const questionDocRef = doc(db, "QuestionLibrary", foundQuestionId);
                  await updateDoc(questionDocRef, { usageHistory: arrayUnion(usageEntry) });
                } else {
                  if (isTestingVenue) {
                    console.log("Skipping usageHistory update for Test Venue run.");
                  } else if (isTestMode) {
                    console.log("Skipping usageHistory update for Test Mode run.");
                  }
                }
              } catch {}
            } catch (finalizeErr) {
              console.error("Error finalizing selection:", finalizeErr);
            }
          }

        } else {
          // *** Fallback logic (preserved) ***
          console.log(`Iterative fetch completed: No UNUSED question found for #${placeholderTag}, Category: ${placeholderCat} at venue ${currentVenue}. Using fallback.`);
          finalQuestionObject = {
            ...qToAdvanceTo,
            Q: `No unused question available for ${originalQField} (${placeholderCat}) at this venue`,
            question: `No unused question available for ${originalQField} (${placeholderCat}) at this venue`,
            CAT: "DISPLAY", type: "DISPLAY", TIME: 5, time: 5, POINTS: 0, points: 0, answers: [],
            A1: null, A2: null, A3: null, A4: null, A5: null, A6: null,
            _placeholderUsed: originalQField, _libraryQuestionId: null,
          };
          try {
            const gameDocRef = doc(db, "games", gameCode);
            const snap = await getDoc(gameDocRef);
            const g = snap.data() || {};
            let questionsArray = [];
            if (Array.isArray(g.questions)) {
              questionsArray = [...g.questions];
            } else if (g.questions) {
              try { questionsArray = Object.values(g.questions); } catch { questionsArray = []; }
            }
            while (questionsArray.length <= targetIdx) questionsArray.push({});
            questionsArray[targetIdx] = finalQuestionObject;
            const qSec = finalQuestionObject.TIME || finalQuestionObject.time || 0;
            const fallbackPayload = {
              selectionStatus: "ready",
              selectionToken: "",
              questionVersion: (Date.now() % 1000000),
              currentQuestion: finalQuestionObject,
              current: targetIdx,
              questions: questionsArray,
            };
            const timerActiveNow = applyAdvanceTimerFields(
              finalQuestionObject,
              qSec,
              fallbackPayload
            );
            await updateDoc(gameDocRef, fallbackPayload);
            
            // Only set current state AFTER DB write completes
            setCurrent(targetIdx);
            setTimer(qSec);
            setTimerActive(timerActiveNow);
            try { scoredRef.current[targetIdx] = false; } catch {}
          } catch {}
        }
      } catch (error) {
         // *** Error handling (preserved) ***
        console.error("Error fetching/replacing placeholder question:", error);
         finalQuestionObject = {
            ...qToAdvanceTo,
            Q: `Error loading question for ${originalQField}`,
            question: `Error loading question for ${originalQField}`,
            CAT: "DISPLAY", type: "DISPLAY", TIME: 5, time: 5, POINTS: 0, points: 0, answers: [],
             A1: null, A2: null, A3: null, A4: null, A5: null, A6: null,
            _placeholderUsed: originalQField, _libraryQuestionId: 'ERROR',
         };
          try {
            const gameDocRef = doc(db, "games", gameCode);
            const snap = await getDoc(gameDocRef);
            const g = snap.data() || {};
            let questionsArray = [];
            if (Array.isArray(g.questions)) {
              questionsArray = [...g.questions];
            } else if (g.questions) {
              try { questionsArray = Object.values(g.questions); } catch { questionsArray = []; }
            }
            while (questionsArray.length <= targetIdx) questionsArray.push({});
            questionsArray[targetIdx] = finalQuestionObject;
            const qSecErr = finalQuestionObject.TIME || finalQuestionObject.time || 0;
            const errPayload = {
              selectionStatus: "ready",
              selectionToken: "",
              questionVersion: (Date.now() % 1000000),
              currentQuestion: finalQuestionObject,
              current: targetIdx,
              questions: questionsArray,
            };
            const timerActiveErr = applyAdvanceTimerFields(
              finalQuestionObject,
              qSecErr,
              errPayload
            );
            await updateDoc(gameDocRef, errPayload);
            
            // Only set current state AFTER DB write completes
            setCurrent(targetIdx);
            setTimer(qSecErr);
            setTimerActive(timerActiveErr);
            try { scoredRef.current[targetIdx] = false; } catch {}
          } catch {}
      }
    } else if (isPlaceholder) { // Invalid placeholder check (preserved)
       console.warn(`Invalid placeholder format or missing data: ${originalQField}`);
       finalQuestionObject = { ...qToAdvanceTo, Q: `Invalid Placeholder: ${originalQField}`, question: `Invalid Placeholder: ${originalQField}`, CAT: "DISPLAY", type: "DISPLAY", TIME: 5, time: 5, POINTS: 0, points: 0, answers: [] };
        setQuestions(prevQs => {
             const updatedQs = [...prevQs];
             updatedQs[targetIdx] = finalQuestionObject;
             return updatedQs;
          });
    }
  }
  // --- END Placeholder Logic ---

  // After selection path, if no placeholder replacement happened, proceed with normal advance
  if (!isPlaceholder) {
    const q = questions[targetIdx] || {};
    const qType = String(q.type || q.CAT || "").toUpperCase();
    const qTime = q.TIME || q.time || 0;
    const hasTimer = Number(qTime) > 0;
    const seconds = hasTimer ? Number(qTime) : 0;

    // Check if this is a non-placeholder JACKPOT question
    if (qType === "JACKPOT" && by > 0) {
      // For non-placeholder JACKPOT, we need to load from QuestionLibrary
      // But first, check if it's already loaded in the questions array
      if (!q.question || q.question.trim() === "" || q.question.includes("Jackpot question - no points")) {
        // Need to load from library - try multiple genres or query without genre filter
        console.log("Non-placeholder JACKPOT detected, attempting to load from QuestionLibrary...");
        const placeholderCat = "JACKPOT";
        
        // Try multiple common genres, then fall back to no genre filter
        const genresToTry = ["General", "Trivia", "Random", ""]; // Empty string means no genre filter
        
        // Use the same loading logic but for a non-placeholder JACKPOT
        try {
          const currentVenue = localStorage.getItem("opt_venue") || "Unknown Venue";
          const BATCH_SIZE = 20;
          const MAX_BATCHES = 5;
          let foundQuestionData = null;
          let foundQuestionId = null;

          for (const genreTag of genresToTry) {
            try {
              let libraryQuery;
              if (genreTag === "") {
                // No genre filter - query all JACKPOT questions without orderBy to avoid index requirement
                // We'll sort client-side instead. Can't paginate without orderBy, so get a larger batch
                libraryQuery = query(
                  collection(db, "QuestionLibrary"),
                  where("questionType", "==", placeholderCat),
                  limit(BATCH_SIZE * 5) // Get larger batch since we can't paginate
                );
              } else {
                let lastVisibleDoc = null;
                
                // Try multiple batches for genre-filtered queries
                for (let batchNum = 0; batchNum < MAX_BATCHES; batchNum++) {
                  libraryQuery = query(
                    collection(db, "QuestionLibrary"),
                    where("genre", "==", genreTag),
                    where("questionType", "==", placeholderCat),
                    orderBy("interestRating", "desc"),
                    limit(BATCH_SIZE)
                  );

                  if (lastVisibleDoc) {
                    libraryQuery = query(libraryQuery, startAfter(lastVisibleDoc));
                  }

                  const librarySnap = await getDocs(libraryQuery);
                  const docsInBatch = librarySnap.docs;

                  if (docsInBatch.length === 0) break;
                  lastVisibleDoc = docsInBatch[docsInBatch.length - 1];

                  for (const docSnap of docsInBatch) {
                    const candidateData = docSnap.data();
                    const candidateId = docSnap.id;
                    const usageHistory = candidateData.usageHistory || [];

                    // Filter out questions with accuracy flags (local filtering)
                    const accuracyFlag = candidateData.accuracyFlag || "";
                    if (accuracyFlag.trim() !== "") {
                      console.log(`Skipping question ${candidateId}: has accuracy flag "${accuracyFlag}"`);
                      continue;
                    }

                    if (usageHistory.length === 0) {
                      foundQuestionData = { id: candidateId, ...candidateData };
                      foundQuestionId = candidateId;
                      break;
                    }
                  }

                  if (foundQuestionData) break;
                  if (docsInBatch.length < BATCH_SIZE) break;
                }
              }

              // For no-genre query, fetch and process all at once
              if (genreTag === "") {
                const librarySnap = await getDocs(libraryQuery);
                let docsInBatch = librarySnap.docs;

                // Sort by interestRating client-side
                docsInBatch = docsInBatch.sort((a, b) => {
                  const ratingA = a.data().interestRating || 0;
                  const ratingB = b.data().interestRating || 0;
                  return ratingB - ratingA; // Descending order
                });

                for (const docSnap of docsInBatch) {
                  const candidateData = docSnap.data();
                  const candidateId = docSnap.id;
                  const usageHistory = candidateData.usageHistory || [];

                  // Filter out questions with accuracy flags (local filtering)
                  const accuracyFlag = candidateData.accuracyFlag || "";
                  if (accuracyFlag.trim() !== "") {
                    console.log(`Skipping question ${candidateId}: has accuracy flag "${accuracyFlag}"`);
                    continue;
                  }

                  if (usageHistory.length === 0) {
                    foundQuestionData = { id: candidateId, ...candidateData };
                    foundQuestionId = candidateId;
                    break;
                  }
                }
              }

              if (foundQuestionData) break; // Found one, stop trying genres
            } catch (genreError) {
              console.warn(`Error querying genre "${genreTag}":`, genreError);
              // Continue to next genre
              continue;
            }
          }

          if (foundQuestionData) {
            const libraryQ = foundQuestionData;
            const libraryCat = (libraryQ.questionType || "").toUpperCase();
            const isBreadcrumbs = libraryCat === "BREADCRUMBS";
            const libraryAnswersRaw = isBreadcrumbs
              ? [libraryQ.answer].filter(a => a != null && String(a).trim() !== "")
              : [libraryQ.answer, libraryQ.A2, libraryQ.A3, libraryQ.A4, libraryQ.A5, libraryQ.A6].filter(a => a != null && String(a).trim() !== "");
            const libraryFacts = isBreadcrumbs ? [libraryQ.A2, libraryQ.A3, libraryQ.A4, libraryQ.A5, libraryQ.A6, libraryQ.A7, libraryQ.A8, libraryQ.A9, libraryQ.A10, libraryQ.A11].filter(a => a != null && String(a).trim() !== "") : undefined;
            
            finalQuestionObject = {
              TIME: q.TIME || q.time || 60,
              time: q.TIME || q.time || 60,
              POINTS: "0",
              points: 0,
              BACKGROUND_URL: q.BACKGROUND_URL || q.background || "",
              question: libraryQ.question || "Error: Question text missing",
              Q: libraryQ.question || "Error: Question text missing",
              CAT: libraryCat,
              type: libraryCat,
              A1: libraryQ.answer || null,
              A2: libraryQ.A2 || null,
              A3: libraryQ.A3 || null,
              A4: libraryQ.A4 || null,
              A5: libraryQ.A5 || null,
              A6: libraryQ.A6 || null,
              A7: libraryQ.A7 || null,
              A8: libraryQ.A8 || null,
              A9: libraryQ.A9 || null,
              A10: libraryQ.A10 || null,
              A11: libraryQ.A11 || null,
              NOTES: libraryQ.notes || "",
              notes: libraryQ.notes || "",
              P1_URL: libraryQ.Medialink || "",
              p1: libraryQ.Medialink || "",
              SOUND_URL: libraryQ.Audiolink || "",
              sound: libraryQ.Audiolink || "",
              JIGSAW: String(q.JIGSAW ?? "").trim() !== "" ? String(q.JIGSAW) : String(libraryQ.jigsaw ?? ""),
              answers: libraryAnswersRaw,
              ...(isBreadcrumbs ? { facts: libraryFacts || [] } : {}),
              answersOrder: getShuffledOrder(libraryAnswersRaw.length),
              maxSelectable: 1,
              _libraryQuestionId: foundQuestionId,
            };

            // Store JACKPOT locally and set paused
            pendingJackpotRef.current = {
              questionData: finalQuestionObject,
              targetIdx: targetIdx,
              questionId: foundQuestionId,
              currentVenue: currentVenue
            };
            setAutoPaused(true);
            setOptAutoplayLocal(false);
            try {
              const gameDocRef = doc(db, "games", gameCode);
              await updateDoc(gameDocRef, {
                selectionStatus: "ready",
                selectionToken: "",
                autoPaused: true,
                opt_autoplay: false
              });
            } catch {}
            console.log("Non-placeholder JACKPOT question loaded and stored locally. Waiting for unpause.");
            // Don't write to game document - wait for unpause
            isAdvancingRef.current = false;
            return; // Exit early - prevent further advance logic
          } else {
            // No question found - show error but still pause
            console.warn("No unused JACKPOT question found in QuestionLibrary");
            setAutoPaused(true);
            setOptAutoplayLocal(false);
            try {
              const gameDocRef = doc(db, "games", gameCode);
              await updateDoc(gameDocRef, {
                current: targetIdx,
                timer: seconds,
                timerActive: hasTimer,
                questionStartTime: Date.now(),
                questionStartTimeServer: serverTimestamp(),
                autoPaused: true,
                opt_autoplay: false,
              });
              
              // Only set current state AFTER DB write completes
              setCurrent(targetIdx);
              setTimer(seconds);
              setTimerActive(hasTimer);
            } catch (e) {
              console.warn("Failed to update game doc for JACKPOT (no question found):", e);
              // Still set state even if DB write fails
              setCurrent(targetIdx);
              setTimer(seconds);
              setTimerActive(hasTimer);
            }
            isAdvancingRef.current = false;
            return; // Exit early - prevent further advance logic
          }
        } catch (error) {
          console.error("Error loading JACKPOT question:", error);
          // On error, still pause but show what we have
          setAutoPaused(true);
          setOptAutoplayLocal(false);
          try {
            const gameDocRef = doc(db, "games", gameCode);
            await updateDoc(gameDocRef, {
              current: targetIdx,
              timer: seconds,
              timerActive: hasTimer,
              questionStartTime: Date.now(),
              questionStartTimeServer: serverTimestamp(),
              autoPaused: true,
              opt_autoplay: false,
            });
            
            // Only set current state AFTER DB write completes
            setCurrent(targetIdx);
            setTimer(seconds);
            setTimerActive(hasTimer);
          } catch (e) {
            console.warn("Failed to update game doc for JACKPOT (error case):", e);
            // Still set state even if DB write fails
            setCurrent(targetIdx);
            setTimer(seconds);
            setTimerActive(hasTimer);
          }
          isAdvancingRef.current = false;
          return; // Exit early - prevent further advance logic
        }
      } else {
        // JACKPOT question already has content - still need to pause it
        setAutoPaused(true);
        setOptAutoplayLocal(false);
        try {
          const gameDocRef = doc(db, "games", gameCode);
          await updateDoc(gameDocRef, {
            current: targetIdx,
            timer: seconds,
            timerActive: hasTimer,
            questionStartTime: (() => {
              const t = Date.now();
              questionStartTimeRef.current = t;
              return t;
            })(),
            questionStartTimeServer: serverTimestamp(),
            autoPaused: true,
            opt_autoplay: false
          });
        } catch (e) {
          console.warn("Failed to update game doc for JACKPOT:", e);
        }
      }
    }

    if (isHost && qType !== "JACKPOT") {
      const timerActiveNow = timerActiveOnQuestionAdvance(q, hasTimer);
      try {
        const advancePayload = { current: targetIdx };
        applyAdvanceTimerFields(q, seconds, advancePayload);
        await updateDoc(doc(db, "games", gameCode), advancePayload);
        
        // Only set current state AFTER DB write completes - prevents rendering stale question data
        setCurrent(targetIdx);
        setTimer(seconds);
        setTimerActive(timerActiveNow);
        try { scoredRef.current[targetIdx] = false; } catch {}
      } catch (e) {
        console.warn("Failed to update game doc for non-placeholder advance:", e);
        // Still set state even if DB write fails, so UI doesn't hang
        setCurrent(targetIdx);
        setTimer(seconds);
        setTimerActive(timerActiveNow);
        try { scoredRef.current[targetIdx] = false; } catch {}
      }
    } else {
      // Non-host or JACKPOT: set state immediately (no DB write needed for non-host)
      const timerActiveNow = timerActiveOnQuestionAdvance(q, hasTimer);
      setCurrent(targetIdx);
      setTimer(seconds);
      setTimerActive(timerActiveNow);
      try { scoredRef.current[targetIdx] = false; } catch {}
    }
  } else {
    // Placeholder path: state already set after DB write above, nothing to do here
    // (This branch should not be reached if placeholder replacement succeeded)
    try { scoredRef.current[targetIdx] = false; } catch {}
  }

  } finally {
    isAdvancingRef.current = false;
  }

}; // End of advance function
const advanceRef = useRef(advance);
advanceRef.current = advance;

useEffect(() => {
  if (!questions || current < 0) return;
  const qType = (questions[current]?.type || "").toUpperCase();

if (qType !== "END QUIZ") {      // <--- Change 'else if' back to 'if'
      setShowQuestionScores(false);
    }

}, [current, questions, tab]);


useEffect(() => {
  // Update the round starting index whenever the current question changes
  const startIndex = findRoundStart(questions, current);
  setRoundQuestionStartIdx(startIndex);
}, [questions, current]);

useEffect(() => {
  if (!gameCode || !questions?.length) return undefined;
  const ven = localStorage.getItem("opt_venue") || "Test Venue";
  const minQ =
    parseInt(localStorage.getItem("opt_team_min_questions_per_tag") || "5", 10) || 5;
  let cancelled = false;
  runPreflightTeamTagPools(ven, questions, minQ)
    .then((r) => {
      if (!cancelled) setTeamTagPreflight(r);
    })
    .catch(() => {});
  return () => {
    cancelled = true;
  };
}, [gameCode, questions]);

useEffect(() => {
  if (!questions?.length) {
    setCsvTeamChoiceOffWarn(false);
    return;
  }
  const has = csvHasTeamChoiceMarkers(questions);
  const en = getTeamRoundChoiceSettingsFromLocalStorage().enabled;
  setCsvTeamChoiceOffWarn(has && !en);
}, [questions]);


const slideEnteredAtRef = useRef(performance.now());
useEffect(() => { slideEnteredAtRef.current = performance.now(); }, [current]);

const [showAnswerReveal, setShowAnswerReveal] = useState(false);

useEffect(() => {
  // This runs *after* 'current' has updated and the component re-renders.
  // It ensures the reveal state is false for the new question initially.
  setShowAnswerReveal(false);
}, [current, setShowAnswerReveal]); // Dependencies: runs when 'current' changes


// ===>>> ADD THIS useEffect HOOK <<<===
useEffect(() => {
  // This function will be called when the event is dispatched from Quizscreen
  const hideOverlay = () => {
    console.log("quiz:hideReveal event received, hiding overlay."); // Optional: for debugging
    setShowAnswerReveal(false); // Call the state setter
  };



  // Listen for the custom event
  window.addEventListener("quiz:hideReveal", hideOverlay);

  // Cleanup function: Remove the listener when Quizmaster unmounts
  return () => {
    window.removeEventListener("quiz:hideReveal", hideOverlay);
  };
}, [setShowAnswerReveal]); // Dependency array includes the state setter
// ===>>> END of useEffect HOOK TO ADD <<<===



// State for Quiz Options
const [quizOptions, setQuizOptions] = useState({
    aiHost: "Ollie",
    joinTime: "90s",
    teamSelfies: "On",
    scoreDisplay: "After Each Question",
    musicVolume: 75,
    vanishingPenalty: "On",
    tieBreaker: "Fastest Answer Wins",
    showTopTeams: "On",
    nameFilter: "On",
    allowRejoin: "On",
    addTestUsers: "Off",
    teamRoundChoiceEnabled: "Off",
    teamRoundChoiceBehavior: "fairShare",
});




// --- Auto-advance controls ---
const [autoPaused, setAutoPaused] = useState(false);
const autoTimerRef = useRef(0);
// Store pending JACKPOT question until unpaused
const pendingJackpotRef = useRef(null);
const keyupIgnoreNextPrevUntilRef = useRef(0);

// Helper: read persisted setting (default Yes)
const getAutoPlaySetting = () => (localStorage.getItem("opt_autoplay") || "Yes") === "Yes";
const [autoPlayOn, setAutoPlayOn] = useState(getAutoPlaySetting());
useEffect(() => {
  // If user changes it in the popup on the display, keep admin in sync on next focus/tick
  const id = setInterval(() => {
    const v = getAutoPlaySetting();
    if (v !== autoPlayOn) setAutoPlayOn(v);
  }, 1000);
  return () => clearInterval(id);
}, [autoPlayOn]);


// Helper to persist autoplay intent locally and keep React state in sync
function setOptAutoplayLocal(on) {
  try {
    localStorage.setItem("opt_autoplay", on ? "Yes" : "No");
  } catch { /* ignore storage errors */ }
  setAutoPlayOn(!!on);
}


// At the top: list of required fields:
const REQUIRED_FIELDS = [
  "CAT", "Q", "A2", "A3", "A4", "A5", "A6",
  "P1", "P2", "P3", "P4", "P5", "P6",
  "SOUND", "VIDEO", "BACKGROUND", "STYLE", "TIME", "NOTES"
];

// Then, for your grid:
const columns = REQUIRED_FIELDS.map(field => ({
  key: field,
  name: field,
  editable: true
}));

const handleEditQuizFile = async (fileRef) => {
  try {
    const url = await getDownloadURL(fileRef);
    const res = await fetch(url);
    const csv = await res.text();

    Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim(),
      complete: async (results) => {
        // ---- Fix the header row: ensure all REQUIRED_FIELDS are present ----
        let fields = results.meta.fields || [];
        let missingFields = REQUIRED_FIELDS.filter(f => !fields.includes(f));
        if (missingFields.length > 0) {
          // Add missing fields to meta.fields
          fields = [...fields, ...missingFields];
          results.meta.fields = fields;
        }

        // ---- Fill each row with missing fields ----
        const qs = results.data.map((q, i) => {
          // Ensure every required field is present (default: "")
          const filled = {};
          for (const field of REQUIRED_FIELDS) filled[field] = (q[field] ?? "");

          const catUpper = String(filled.CAT || filled.type || filled.cat || filled.TYPE || filled.questionType || q.CAT || "").trim().toUpperCase();
          const isBreadcrumbs = catUpper === "BREADCRUMBS";
          const answers = isBreadcrumbs
            ? [q.A1].filter((a) => !!a)
            : [filled.A1, filled.A2, filled.A3, filled.A4, filled.A5, filled.A6].filter((a) => !!a);
          const facts = isBreadcrumbs ? [q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11].filter((a) => !!a) : undefined;

          let maxSelectable = 1;
          if (catUpper === "MULTIANSWER") {
            maxSelectable = answers.filter((a) => /^\s*\[.*?\]\s*$/.test(a)).length;
          }
          const answersOrder = getShuffledOrder(answers.length);
          const rawTime = typeof filled.TIME !== "undefined" && filled.TIME !== ""
            ? Number(filled.TIME) : undefined;
          const time = !isNaN(rawTime) && rawTime !== null
            ? rawTime
            : settings.questionTimer || 20;

          return {
            question: filled.Q,
            answers,
            ...(isBreadcrumbs ? { facts: facts || [] } : {}),
            type: filled.CAT,
            sound: filled.SOUND,
            video: filled.VIDEO,
            background: filled.BACKGROUND,
            p1: filled.P1,
            answersOrder,
            maxSelectable,
            vanishOrder:
              catUpper === "VANISHINGCHOICE"
                ? getRandomVanishOrder(answersOrder)
                : null,
            time,
            ...filled // ⚡️ Add ALL fields for the editor!
          };
        });

        // Warn if columns were missing
        if (missingFields.length) {
          appAlert("This CSV was missing columns: " + missingFields.join(", ") + ". They will be blank until edited or saved.");
        }

        // Optionally resolve media URLs if needed
        const fixed = qs;  // or just pass the parsed questions as-is



        setQuestions(fixed);
        setCurrent(0);
        setEditMode(true);
        setStep(3); // Or whatever triggers your edit UI
      }
    });
  } catch (err) {
    appAlert("Error opening quiz for edit: " + err.message);
  }
};





// Load the user's display name for the top bar
useEffect(() => {
  if (!user?.uid) {
    setProfileName("");
    return;
  }
  let unsub = () => {};
  try {
    unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const d = snap.data() || {};
      const name =
        d.displayName ||
        d.name ||
        d.profile?.displayName ||
        user.displayName ||
        user.email ||
        "";
      setProfileName(name);
    });
  } catch {
    // Fall back to Auth object if users/<uid> doc doesn't exist
    setProfileName(user.displayName || user.email || "");
  }
  return () => unsub && unsub();
}, [user?.uid]);




useEffect(() => {
  if (!showReconnect) return;
  setLoadingGames(true);
  setReconnectError("");
  getDocs(collection(db, "games")).then(snap => {
    // Only show games that are "active" or have a state you care about.
    // You may want to filter out finished or old games here!
    const games = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setActiveGames(games);
    setLoadingGames(false);
  }).catch(err => {
    setReconnectError("Failed to load games.");
    setLoadingGames(false);
  });
}, [showReconnect]);


useEffect(() => {
  let profileUnsubscribe = () => {}; // Function to stop listening to profile changes

  const authUnsubscribe = onAuthStateChanged(auth, (user) => {
    // Stop listening to the old user's profile when auth state changes
    profileUnsubscribe();

    if (user) {
      setUser(user);
      // This is the reference to the document shown in your screenshot
      const userDocRef = doc(db, "users", user.uid);

      // Set up a REAL-TIME listener for the user's document
      profileUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          // Store the entire user document (including the 'role' field) in state
          setUserProfile(docSnap.data());
        } else {
          // Handle case where user is authenticated but has no profile document
          console.warn("User profile document not found in Firestore for UID:", user.uid);
          setUserProfile({}); // Use an empty object as a safe fallback
        }
        setChecking(false);
      });
    } else {
      // User logged out, clear everything
      setUser(null);
      setUserProfile(null);
      setChecking(false);
    }
  });

  // Cleanup function to stop both listeners when the component unmounts
  return () => {
    authUnsubscribe();
    profileUnsubscribe();
  };
}, []);







// Wheel of Fortune letter reveal effect
useEffect(() => {
  // Only for WHEEL type
  if (!questions || !questions[current] || (questions[current].type || "").toUpperCase() !== "WHEEL") {
    setRevealedIndexes([]);
    return;
  }
  const phrase = questions[current].question || "";
  // Don't reveal spaces or punctuation, just letters
  const letterIndexes = [];
  for (let i = 0; i < phrase.length; i++) {
    if (/[A-Z]/i.test(phrase[i])) letterIndexes.push(i);
  }
  // Reveal one letter at a time, randomly, every 450ms
  let revealOrder = [...letterIndexes];
  for (let i = revealOrder.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [revealOrder[i], revealOrder[j]] = [revealOrder[j], revealOrder[i]];
  }
  setRevealedIndexes([]); // Reset when question changes

  let revealed = [];
  let idx = 0;
  const interval = setInterval(() => {
    if (idx >= revealOrder.length) {
      clearInterval(interval);
      return;
    }
    revealed = [...revealed, revealOrder[idx]];
    setRevealedIndexes([...revealed]);
    idx++;
  }, 450);

  return () => clearInterval(interval);
}, [current, questions]);


useEffect(() => {
  if (scrollRef.current) {
    setRowWidth(scrollRef.current.scrollWidth / 2);
  }
}, [topTeams.length]);

// Playable-question count per quiz file name, filled in after the list loads.
const [quizQuestionCounts, setQuizQuestionCounts] = useState({});

// ==============================
// EFFECT: call it and set your existing quiz list state
// ==============================
// Load quizzes I own or that are shared with me (via Firestore index)
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!user?.uid) return;
    const list = await fetchQuizzesIncludingShares(auth.currentUser);
    if (!cancelled) setQuizFiles(list);
    setLoadingQuizzes(false);
    if (!cancelled) {
      loadQuestionCounts(
        list,
        (name, count) => setQuizQuestionCounts((prev) => ({ ...prev, [name]: count })),
        () => cancelled
      );
    }
  })();
  return () => { cancelled = true; };
}, [user?.uid]);




// Listen for teams & their selfies
useEffect(() => {
  if (!gameCode) return; // Don't run if no gameCode yet
  const unsub = onSnapshot(
    collection(db, "games", gameCode, "teams"),
    (snap) => {
      const info = {};
      snap.forEach((doc) => {
        const data = doc.data();
        info[data.team] = data.selfieUrl || null;
      });
      setTeamInfo(info);
    }
  );
  return unsub;
}, [gameCode]);








  useEffect(() => {
    const q = questionsRef.current[current];
    const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "").trim().toUpperCase();
    const isVanishingChoice = qType === "VANISHINGCHOICE";
    const baseAnswersOrder = Array.isArray(q?.answersOrder) && q.answersOrder.length
      ? q.answersOrder
      : Array.from({ length: Array.isArray(q?.answers) ? q.answers.length : 0 }, (_, i) => i);

    vanishTimerIdsRef.current.forEach(clearTimeout);
    vanishTimerIdsRef.current = [];
    vanishStartedForRef.current = null;

    if (q && isVanishingChoice) {
      const arr = [...baseAnswersOrder];
      setVanishingPlan(null);
      setVanishingIndexes(arr);
      if (isHost && gameCode) {
        updateDoc(doc(db, "games", gameCode), {
          vanishingIndexes: arr,
          vanishingPlan: null,
        }).catch(() => {});
      }
    } else if (q) {
      setVanishingIndexes([...baseAnswersOrder]);
      if (isHost && gameCode) {
        updateDoc(doc(db, "games", gameCode), {
          vanishingIndexes: [...baseAnswersOrder],
        }).catch(() => {});
      }
    }

    return () => {
      vanishTimerIdsRef.current.forEach(clearTimeout);
      vanishTimerIdsRef.current = [];
    };
    // eslint-disable-next-line
  }, [current, gameCode, isHost]);

  const startVanishingChoiceSchedule = useCallback(
    (qIdx) => {
      if (!isHost || !gameCode) return;
      if (qIdx !== current) return;

      const q = questionsRef.current[qIdx];
      const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "")
        .trim()
        .toUpperCase();
      if (qType !== "VANISHINGCHOICE") return;
      if (vanishStartedForRef.current === qIdx) return;
      vanishStartedForRef.current = qIdx;

      vanishTimerIdsRef.current.forEach(clearTimeout);
      vanishTimerIdsRef.current = [];

      const baseAnswersOrder = Array.isArray(q?.answersOrder) && q.answersOrder.length
        ? q.answersOrder
        : Array.from({ length: Array.isArray(q?.answers) ? q.answers.length : 0 }, (_, i) => i);

      let arr = [...baseAnswersOrder];
      const minChoices = 2;
      const vanishOrder =
        Array.isArray(q.vanishOrder) && q.vanishOrder.length
          ? q.vanishOrder
          : getRandomVanishOrder(arr);

      const totalSec = Math.max(1, getQuestionDurationSeconds(q, settings?.questionTimer ?? 20));
      const plan = buildVanishingChoicePlan(totalSec, arr, vanishOrder, minChoices);
      const visibleNow = computeVanishingIndexesFromPlan(plan, totalSec);

      setVanishingPlan(plan);
      setVanishingIndexes(visibleNow);
      updateDoc(doc(db, "games", gameCode), {
        vanishingPlan: plan,
        vanishingIndexes: visibleNow,
      });
    },
    [current, gameCode, isHost, settings]
  );

  const startVanishingChoiceScheduleRef = useRef(startVanishingChoiceSchedule);
  startVanishingChoiceScheduleRef.current = startVanishingChoiceSchedule;

  const handleAnswersGateOpened = useCallback((qIdx) => {
    if (!isHost || qIdx !== current) return;
    const q = questionsRef.current[qIdx];
    const sec = getQuestionDurationSeconds(q, timerRef.current);
    if (sec > 0 && shouldDeferTimerUntilAnswersGate(q)) {
      const t = Date.now();
      questionStartTimeRef.current = t;
      timerRef.current = sec;
      timerActiveRef.current = true;
      setTimer(sec);
      setTimerActive(true);
    }
    startVanishingChoiceScheduleRef.current?.(qIdx);
  }, [current, isHost]);

  // Timer-synced vanishing: host and player derive visibility from the same countdown + plan.
  useEffect(() => {
    if (!vanishingPlan) return;
    const q = questionsRef.current[current];
    const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "")
      .trim()
      .toUpperCase();
    if (qType !== "VANISHINGCHOICE") return;

    const next = computeVanishingIndexesFromPlan(vanishingPlan, timer);
    setVanishingIndexes((prev) => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
      if (isHost) {
        const removed = prev.filter((x) => !next.includes(x));
        if (removed.length) {
          setJustVanished((jv) => [...new Set([...jv, ...removed])]);
          setTimeout(() => {
            setJustVanished((jv) => jv.filter((x) => !removed.includes(x)));
          }, VANISH_POOF_MS);
        }
      }
      return next;
    });
  }, [timer, vanishingPlan, current, isHost]);

  // Safety net: always end vanishing choice with exactly two visible options.
  useEffect(() => {
    if (!isHost || !gameCode || timer !== 0 || !vanishingPlan) return;
    const q = questionsRef.current[current];
    const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "")
      .trim()
      .toUpperCase();
    if (qType !== "VANISHINGCHOICE") return;

    setVanishingIndexes((prev) => {
      const finalized = finalizeVanishingIndexes(prev, 2);
      if (finalized.length === prev.length) return prev;
      updateDoc(doc(db, "games", gameCode), { vanishingIndexes: finalized }).catch(() => {});
      return finalized;
    });
  }, [timer, current, isHost, gameCode, vanishingPlan]);

  useEffect(() => {
    // Declare an unsubscribe function for the Firestore listener (default to a no-op)
    let unsub = () => {};

    // Only activate the listener for NEARESTANSWER and JACKPOT questions, after the timer runs out
    const questionType = String(questions?.[current]?.type || questions?.[current]?.CAT || "").toUpperCase();
    if ((questionType === "NEARESTANSWER" || questionType === "JACKPOT") && timer === 0) {
      // Reference the 'answers' subcollection in Firestore
      const collRef = collection(db, "games", gameCode, "answers");

      // Set up a real-time listener on the 'answers' collection
      unsub = onSnapshot(collRef, (answersSnap) => {
        // Prepare an array to hold relevant answers for this question
        const teamAnswers = [];

        // Loop through all documents in 'answers'
        answersSnap.forEach((docSnap) => {
          const data = docSnap.data();

          // Filter: Only include answers for the current question, with an answer present
          if (
            data.q === current &&
            data.answer !== undefined &&
            data.answer !== null
          ) {
            teamAnswers.push({
              team: data.team, // Team name
              answer: data.answer, // Their answer value
              points: data.points, // The awarded points (from Quizmaster)
            });
          }
        });

        // Update the React state, triggering a re-render with the latest answers/points
        setAllAnswers(teamAnswers);
      });
    } else {
      // For other types (or before timer runs out), clear the state so nothing is displayed
      setAllAnswers([]);
    }

    // Clean up: Unsubscribe from Firestore when effect is re-run or component unmounts
    return () => unsub();
  }, [timer, current, questions]); // Effect runs whenever timer, current, or questions change

  // Pause JACKPOT questions when timer hits 0 (after results are shown)
  useEffect(() => {
    if (!gameCode) return;
    const q = questions?.[current];
    const isJackpot = String(q?.type || q?.CAT || "").toUpperCase() === "JACKPOT";
    if (isJackpot && timer === 0 && !autoPaused) {
      // Timer just hit 0 for JACKPOT - pause it to keep results visible until host presses P
      setAutoPaused(true);
      try {
        localStorage.setItem("opt_autoplay", "No");
      } catch {}
      try {
        updateDoc(doc(db, "games", gameCode), {
          autoPaused: true,
          opt_autoplay: false
        });
      } catch {}
    }
  }, [timer, current, questions, gameCode, autoPaused]);

// REPLACE the whole function in Quizmaster.js
// REPLACE the whole function in Quizmaster.js
async function scoreNearestAnswer(current, correctValue) {
  // Pull all answers for this question
  const answersSnap = await getDocs(collection(db, "games", gameCode, "answers"));

  const teamAnswers = [];
  answersSnap.forEach((docSnap) => {
    const d = docSnap.data();
    if (d.q !== current || d.answer === undefined || d.answer === null) return;

    // Normalise a submission timestamp in *milliseconds* for tie-breaks
    const submittedAtMs =
      (d.submittedAt && typeof d.submittedAt.toMillis === "function")
        ? d.submittedAt.toMillis()
        : (typeof d.submittedAt === "number"
            ? d.submittedAt
            : (typeof d.time === "number" ? d.time : Number.POSITIVE_INFINITY));

    teamAnswers.push({
      team: d.team,
      answer: parseFloat(Array.isArray(d.answer) ? d.answer[0] : d.answer),
      submittedAtMs,
    });
  });

  // Sort: 1) smallest absolute distance; 2) earliest submittedAt (fastest wins ties)
  teamAnswers.sort((a, b) => {
    const da = Math.abs(a.answer - correctValue);
    const db = Math.abs(b.answer - correctValue);
    if (da !== db) return da - db;
    return a.submittedAtMs - b.submittedAtMs;
  });

  // Award points to the top N (use your existing NEAREST_POINTS array)
  const batch = writeBatch(db);
  const awardedTeams = new Set();

  teamAnswers.forEach((entry, idx) => {
    const pts = NEAREST_POINTS[idx] || 0;
    awardedTeams.add(entry.team);
    batch.set(
      doc(db, "games", gameCode, "answers", `${current}-${entry.team}`),
      {
        points: pts,
        awarded: true,
        q: current,
        team: entry.team,
        answer: entry.answer,
      },
      { merge: true }
    );
  });

  // Ensure everyone who answered is marked awarded with 0 if they didn't place
  answersSnap.forEach((docSnap) => {
    const d = docSnap.data();
    if (d.q !== current || d.answer === undefined || d.answer === null) return;
    if (awardedTeams.has(d.team)) return;

    batch.set(
      doc(db, "games", gameCode, "answers", `${current}-${d.team}`),
      {
        points: 0,
        awarded: true,
        q: current,
        team: d.team,
        answer: d.answer,
      },
      { merge: true }
    );
  });

  await batch.commit();
}



// ──────────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────────


  useEffect(() => {
    setSelectedAnswers([]);
  }, [current]);

  useEffect(() => {
    // This only runs once at mount
    getDownloadURL(ref(storage, "media/Brainbackground.png")).then(setBgUrl);
  }, []);

  useEffect(() => {
    getDownloadURL(ref(storage, "media/" + imageFilename)).then(
      setHardcodedUrl
    );
  }, []);

  useEffect(() => {
    if (!timerActive) return;
    if (timer <= 0) {
      setTimerActive(false);
      updateDoc(doc(db, "games", gameCode), {
        timer: 0,
        timerActive: false,
      });
      return;
    }
    // Local countdown for UI only - no database writes
    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, timer]);

  const timerActiveRef = useRef(timerActive);
  useEffect(() => {
    timerActiveRef.current = timerActive;
  }, [timerActive]);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);



useEffect(() => {
  if (!gameCode) return;
  const unsub = onSnapshot(doc(db, "games", gameCode), (snap) => {
    setJoinCode(snap.data()?.joinCode || "");
  });
  return unsub;
}, [gameCode]);



  //
  // Just update the timer if we are NOT the host, or timerActive is false.
  // (You already do this correctly in your onSnapshot useEffect.)
useEffect(() => {
  if (!gameCode) return; // Don't run until we have a valid gameCode
  const unsub = onSnapshot(doc(db, "games", gameCode), (snap) => {
    const data = snap.data();
if (data) { // Check if data exists at all
      setSelectionStatus(data.selectionStatus || "idle");
      // Prefer single-flight currentQuestion flow
      const selStatus = data.selectionStatus || "idle";
      const version = typeof data.questionVersion === "number" ? data.questionVersion : 0;
      if (data.currentQuestion && selStatus === "ready" && version > (lastSeenQuestionVersionRef.current || 0)) {
        const idx = typeof data.current === "number" ? data.current : current;
        setQuestions((prev) => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          while (next.length <= idx) next.push({});
          next[idx] = data.currentQuestion;
          return next;
        });
        lastSeenQuestionVersionRef.current = version;
      }

      // Fallback only if there is no finalized currentQuestion to apply
      if (!data.currentQuestion && data.questions !== undefined && selStatus !== "selecting") {
        // Fallback to existing array sync only when not selecting
        if (Array.isArray(data.questions)) {
          setQuestions(data.questions);
        } else {
          console.error("Firestore Sync Error: Received 'questions' field is not an array!", data.questions);
        }
      }
      // Safely set other fields
      if (data.settings) setSettings(data.settings);
      if (typeof data.current === "number") setCurrent(data.current);
      if (data.vanishingPlan) {
        setVanishingPlan(data.vanishingPlan);
      } else {
        setVanishingPlan(null);
      }
      // Legacy fallback when no plan is stored (older games / non-vanishing)
      if (!data.vanishingPlan && !isHost && Array.isArray(data.vanishingIndexes)) {
        setVanishingIndexes(data.vanishingIndexes);
      }
    }

    // Only update timer from DB if NOT host, or timer is not running.
    if (
      typeof data?.timer === "number" &&
      (!isHost || !timerActiveRef.current)
    ) {
      setTimer(data.timer);
      // DO NOT set timerActive here.
    }
  });
  return unsub;
}, [isHost, gameCode]); // <-- Add gameCode to dependencies!


  useEffect(() => {
    const handleKeyToggleTopBar = (e) => {
      if (e.key === "ArrowDown") {
        setShowTopBar(false);
      } else if (e.key === "ArrowUp") {
        setShowTopBar(true);
      }
    };

    window.addEventListener("keydown", handleKeyToggleTopBar);
    return () => window.removeEventListener("keydown", handleKeyToggleTopBar);
  }, []);

// Lightweight host overlay while selecting next question
useEffect(() => {
  const prev = selectingOverlayRef.current;
  if (selectionStatus === "selecting") {
    if (!prev) {
      const div = document.createElement("div");
      div.style.position = "fixed";
      div.style.top = "20px";
      div.style.right = "20px";
      div.style.zIndex = "9999";
      div.style.background = "rgba(0,0,0,0.75)";
      div.style.color = "#fff";
      div.style.padding = "10px 14px";
      div.style.borderRadius = "8px";
      div.style.boxShadow = "0 6px 18px rgba(0,0,0,0.4)";
      div.style.font = "600 14px system-ui, -apple-system, Segoe UI, Roboto, Arial";
      div.textContent = "Selecting next question…";
      document.body.appendChild(div);
      selectingOverlayRef.current = div;
    }
  } else {
    if (prev && prev.parentNode) {
      prev.parentNode.removeChild(prev);
      selectingOverlayRef.current = null;
    }
  }
  return () => {
    const el = selectingOverlayRef.current;
    if (el && el.parentNode) el.parentNode.removeChild(el);
    selectingOverlayRef.current = null;
  };
}, [selectionStatus]);


useEffect(() => {
  if (current > 0) setHasEnteredQuiz(true);
}, [current]);

useEffect(() => {
  if (current === 0) setShowQuizOptions(true);
}, [current]);


useEffect(() => {
  if (!hasEnteredQuiz) { setSelfiesReady(false); return; }

  const urls = Array.from(new Set(
    Object.values(teamInfo || {}).filter(Boolean)
  ));
  
  // If there are no selfies, we are ready immediately.
  if (urls.length === 0) { 
    setSelfiesReady(true); 
    return; 
  }

  let active = true;
  let loaded = 0;
  
  const done = () => {
    if (active) {
      setSelfiesReady(true);
    }
  };

  // Start in the loading state
  setSelfiesReady(false);
  
  urls.forEach((url) => {
    // Call your original preload function
    ensurePreloadLink(url); 

    // This is the image loading logic
    const img = new Image();
    img.onload = () => {
      loaded++;
      if (loaded === urls.length) {
        done(); // All images are loaded, set selfies to ready
      }
    };
    img.onerror = () => { // Don't let a broken image stop the flow
      loaded++;
      if (loaded === urls.length) {
        done();
      }
    };
    img.src = url; // Start loading the image
  });

  return () => {
    active = false;
  };
}, [hasEnteredQuiz, teamInfo]);

// You also need to modify 'ensurePreloadLink' in Quizmaster.js to return the link
// In Quizmaster.js, around line 1284:
function ensurePreloadLink(url) {
  if (!url) return null; // ✅ FIX: Return null if no url
  const key = `preload-${url}`;
  
  // Check if it *already exists*
  const existing = document.querySelector(`link[data-preload="${key}"]`);
  if (existing) return existing; // ✅ FIX: Return existing if found

  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("data-preload", key);
  document.head.appendChild(link);
  return link; // ✅ FIX: Return the newly created link
}


// ──────────────────────────────────────────────────────────────────────────────
// CONSOLIDATED SCORING useEffect - Runs when timer hits 0 for ALL scorable types
// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────
// CORRECTED CONSOLIDATED SCORING useEffect - Runs when timer hits 0
// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────
// REFACTORED CONSOLIDATED SCORING useEffect - Runs when timer hits 0
// ──────────────────────────────────────────────────────────────────────────────
// Replace the useEffect hook starting around line 1017
useEffect(() => {
    // Exit if prerequisites aren't met or timer is still running
    if (!questions || !questions[current] || timer !== 0) return;

    const q = questions[current];
    const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "").trim().toUpperCase();
    const correctIdx = 0; // Assuming correct is always index 0 for relevant types

    // --- Define Question Type Groups (Keep these as they are) ---
    const nonScoringTypes = [
        "DISPLAY", "SLIDE", "DEFAULT", "DEFAULT CATEGORY",
        "STARTROUND", "ENDROUND", // <<<--- ADD ENDROUND HERE
        "SHOWROUNDS", "JOIN QUIZ", "INTRODUCTION", "END QUIZ",
        "BREAK TIME"
     ];
    const showScoresImmediatelyTypes = ["WHEEL", "TYPE", "BREADCRUMBS", "VANISHINGCHOICE", "NEARESTANSWER"]; // VANISHING now immediate
    const showScoresDelayedTypes = ["OUTOFORDER", "BETANSWER"];
    const showAnswerRevealTypes = ["MULTIANSWER", "MULTICHOICE"];
    const advanceImmediatelyTypes = [];
    const needsBatchFetchTypes = ["TRUE/FALSE", "MULTIANSWER", "MULTICHOICE", "BETANSWER"];

    // Exit for non-scoring types
// Exit for non-scoring types
    if (nonScoringTypes.includes(qType)) {
        console.log(`Skipping scoring for non-scoring type: ${qType} on question ${current}`); // Add log
        // --- Explicitly mark as "scored" to prevent re-entry ---
        scoredRef.current[current] = true;
        // --- End explicit mark ---
        return; // <<<--- This return prevents scoring attempt
    }

    // --- Check scoredRef *before* the setTimeout ---
    // Prevent setting multiple timeouts if the effect somehow re-runs quickly
    if (scoredRef.current[current]) {
        return;
    }

    // Delay all scoring by 4 seconds
    const delay = setTimeout(() => {
        // --- Double-check scoredRef *inside* timeout before scoring ---
        if (scoredRef.current[current]) {
            console.log(`Scoring for question ${current} already flagged.`);
            return; // Already scored or score attempt in progress
        }
        
        // --- Mark as scoring attempt started (prevents re-entry via this timeout) ---
        // We will only set this to true *after* success later, but this inner check stops parallel runs
        // If you still face issues, you might need a separate 'scoringInProgress' ref flag here.

        (async () => {
            let scoringError = null;
            try {
                // --- Clear WHEEL interval (Keep this) ---
                if (qType === "WHEEL" || qType === "TYPE") {
                  if (typeof wheelIntervalRef !== 'undefined' && wheelIntervalRef.current) { // Added safety check
                    clearInterval(wheelIntervalRef.current);
                    wheelIntervalRef.current = null;
                  }
                }

                // --- Add test answers (Keep this) ---
                const configuredTestUsers = getConfiguredTestUserCount();
                if (configuredTestUsers > 0) {
                    await addTestAnswersForQuestion(db, gameCode, q, current, configuredTestUsers);
                }

                // --- Fetch data for batch types (Keep this) ---
                let answersSnap;
                let batch;
                if (needsBatchFetchTypes.includes(qType)) {
                    const answersRef = collection(db, "games", gameCode, "answers");
                    answersSnap = await getDocs(answersRef);
                    batch = writeBatch(db);
                }

                // --- Execute Scoring Logic for ALL types ---
                console.log(`Attempting to score question ${current} (Type: ${qType})`);
                
                // JACKPOT: No scoring (no points, scored manually if needed)
                if (qType === "JACKPOT") {
                    console.log(`Skipping scoring for JACKPOT question ${current} - no points assigned`);
                    scoredRef.current[current] = true;
                    return; // Exit early, no scoring
                }

                if (qType === "BREADCRUMBS") {
                    await scoreBreadcrumbsQuestion(gameCode, current, q);
                } else if (qType === "WHEEL" || qType === "TYPE") {
                    const correctForType = (q.answers || []).filter(Boolean);
                    const correctArg = qType === "TYPE" && correctForType.length > 0 ? correctForType : (q.answers && q.answers[0]);
                    if (correctArg != null) await scoreWheelQuestion(gameCode, current, correctArg, q);
                } else if (qType === "OUTOFORDER") {
                    await scoreOutOfOrder(gameCode, current, q);
                } else if (qType === "VANISHINGCHOICE") {
                    await scoreVanishingChoice(gameCode, current, correctIdx, q);
                } else if (qType === "NEARESTANSWER") {
                    const correctValue = parseFloat(q.answers[0]);
                    await scoreNearestAnswer(current, correctValue);
                } else if (qType === "TRUE/FALSE") {
                    // ... (keep existing TRUE/FALSE logic using 'batch' and 'answersSnap') ...
                     answersSnap.forEach((docSnap) => {
                         const data = docSnap.data();
                         if (Number(data.q) === Number(current) && data.answer !== undefined && data.team) {
                            // Players already save the original answer index (answersOrder applied on the phone),
                            // same as MULTICHOICE — mapping through answersOrder again flipped TRUE/FALSE scores.
                            let originalIdx = Array.isArray(data.answer) ? data.answer[0] : data.answer;
                            if (typeof originalIdx === "string") originalIdx = parseInt(originalIdx, 10);
                            const isCorrect = originalIdx === 0;
                            const maxPoints = typeof q.points === "number" ? q.points : Number(q.points) || 0;
                            const awardedPts = isCorrect ? maxPoints : 0;
                            batch.set(doc(db, "games", gameCode, "answers", `${current}-${data.team}`), { ...data, correct: isCorrect, points: awardedPts, awarded: true }, { merge: true });
                         }
                    });
                    await batch.commit();                    
                } else if (qType === "MULTIANSWER") {
                    // ... (keep existing MULTIANSWER logic using 'batch' and 'answersSnap') ...
                    const correctIndexes = (q.answers || []).map((ans, idx) => /^\s*\[.*?\]\s*$/.test(String(ans)) ? idx : -1).filter(idx => idx !== -1);
                    const pointsPerCorrect = typeof q.points === "number" ? q.points : Number(q.points) || 0;
                    answersSnap.forEach((docSnap) => {
                        const data = docSnap.data();
                        if (Number(data.q) === Number(current) && Array.isArray(data.answer) && data.team) {
                            const submittedIndexes = data.answer;
                            let correctCount = 0;
                            submittedIndexes.forEach(idx => { if (correctIndexes.includes(idx)) correctCount++; });
                            const awardedPts = correctCount * pointsPerCorrect;
                            const isCorrect = correctCount > 0;
                            batch.set(doc(db, "games", gameCode, "answers", `${current}-${data.team}`), { ...data, correct: isCorrect, points: awardedPts, awarded: true }, { merge: true });
                        }
                    });
                    await batch.commit();                    
                } else if (qType === "MULTICHOICE") {
                    // ... (keep existing MULTICHOICE logic using 'batch' and 'answersSnap') ...
                    answersSnap.forEach((docSnap) => {
                        const data = docSnap.data();
                        if (Number(data.q) === Number(current) && data.answer !== undefined && data.team) {
                            const originalIdx = Array.isArray(data.answer) ? data.answer[0] : data.answer;
                            const isCorrect = originalIdx === 0;
                            const maxPoints = typeof q.points === "number" ? q.points : Number(q.points) || 0;
                            const awardedPts = isCorrect ? maxPoints : 0;
                            batch.set(doc(db, "games", gameCode, "answers", `${current}-${data.team}`), { ...data, correct: isCorrect, points: awardedPts, awarded: true }, { merge: true });
                        }
                    });
                    await batch.commit();                    
                } else if (qType === "BETANSWER") {
                    // ... (keep existing BETANSWER logic using 'batch' and 'answersSnap') ...
                    const teamSnaps = await getDocs(collection(db, "games", gameCode, "teams"));
                    const teamMap = {};
                    teamSnaps.forEach(doc => { teamMap[doc.id] = doc.data(); });
                    answersSnap.forEach((docSnap) => {
                        const data = docSnap.data();
                        if (Number(data.q) === Number(current) && data.answer !== undefined && data.team) {
                            const originalIdx = Array.isArray(data.answer) ? data.answer[0] : data.answer;
                            const isCorrect = originalIdx === 0;
                            const teamDoc = teamMap[data.team];
                            const bet = typeof teamDoc?.currentbet === "number" ? teamDoc.currentbet : 0;
                            const awardedPts = isCorrect ? bet : -bet;
                            batch.set(doc(db, "games", gameCode, "answers", `${current}-${data.team}`), { ...data, correct: isCorrect, points: awardedPts, awarded: true }, { merge: true });
                        }
                    });
                    await batch.commit();                    
                }
                // *** END of specific scoring logic ***

                // --- SET SCORED FLAG *AFTER* ALL SCORING ATTEMPTS ---
                scoredRef.current[current] = true; // Moved here!
                console.log(`Scoring successful for question ${current}. Flag set.`);

            } catch (error) {
                scoringError = error; // Store error
                console.error(`Scoring failed for question ${current}:`, error);
                // scoredRef remains false here to allow potential retry if needed
            }

            // --- Execute Post-Scoring Actions (Keep this logic) ---
            if (!scoringError) {
                if (showScoresImmediatelyTypes.includes(qType)) {
                    setLastQuestionIdx(current);
                    setShowQuestionScores(true);
                } else if (showScoresDelayedTypes.includes(qType)) {
                    setTimeout(() => {
                        setLastQuestionIdx(current);
                        setShowQuestionScores(true);
                    }, 3000);


                } else if (showAnswerRevealTypes.includes(qType)) {
                    setShowAnswerReveal(true); // Assuming setShowAnswerReveal is defined
                } else if (advanceImmediatelyTypes.includes(qType)) {
                    advance(1, true); // Assuming advance function is defined


                    
                } else if (qType === "NEARESTANSWER" || qType === "WHEEL" || qType === "TRUE/FALSE" || qType === "BREADCRUMBS") {
                    // No automatic action needed after scoring NEAREST / WHEEL / BREADCRUMBS
                } else {
                    // Fallback for unknown scorable types (maybe advance?)
                    console.warn(`Unknown scorable type ${qType} - advancing.`);
                    advance(1, true);
                }
                
            } else {
                // Handle scoring error case - maybe log or just advance?
                console.error(`Advancing after scoring error on question ${current}.`);
                advance(1, true);
            }

        })(); // End async IIFE

    }, 4000); // 4-second delay remains

    // Cleanup function
    return () => {
        clearTimeout(delay);
    };

// Keep dependencies - ensure `advance` and `setShowAnswerReveal` are stable or included if needed
}, [timer, current, questions, gameCode]);
// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────

  // ---- Helpers ----
// Clean filename, ignore everything after extension, trim whitespace
// Put this above resolveMediaUrls
// Put this above resolveMediaUrls
function cleanFilename(name) {
  const raw = String(name || "").trim().replace(/^["']|["']$/g, "");
  const base = raw.replace(
    /^(.+?\.(mp3|m4a|wav|aac|ogg|flac|mp4|m4v|mov|wmv|webm|jpg|jpeg|png|gif|webp|bmp|svg)).*$/i,
    "$1"
  );
  if (!base) return "";
  // prefer underscores because many uploads store that way
  return base.replace(/\s+/g, "_");
}



  function getAllMediaFilenames(questions) {
    const mediaFields = ["sound", "video", "background", "p1"];
    const files = new Set();
    for (const q of questions) {
      for (const field of mediaFields) {
        if (q[field]) files.add(cleanFilename(q[field]));
      }
    }
    return Array.from(files);
  }

function findFile(fileList, filename) {
  if (!fileList || !filename || typeof filename !== "string") return null;
  const clean = cleanFilename(filename).toLowerCase();
  return Array.from(fileList).find((f) => f && f.name && f.name.toLowerCase() === clean) || null;
}

async function uploadAndReplaceMedia(questions, filesToUpload, setProgress) {
  let count = 0;

  for (const q of questions) {
    for (const field of ["sound", "video", "background", "p1"]) {
      if (q && typeof q[field] === "string" && q[field].trim()) {
        // filename from CSV/UI (may include {...} metadata or spaces)
        const baseFile = cleanFilename(q[field]);
        if (!baseFile) continue;

        // try to find a matching File object from the picker
        const file = findFile(filesToUpload, baseFile);
        if (file) {
          try {
            // ✅ use the actual file’s name, cleaned for storage
            const safeName = cleanFilename(file.name);
            const fileRef = ref(storage, "media/" + safeName);

            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);

            // write the newly uploaded URL back to the question
            q[field] = url;
          } catch (err) {
            console.error("Error uploading file:", file.name, err);
            // keep original value (likely a filename)
          }
        } else {
          // no local file chosen for this reference — leave as-is
        }
      }
    }
    count++;
    if (setProgress) setProgress(count / questions.length);
  }

  return questions;
}

// FILE: Quizmaster.js
// REPLACE the whole uploadSelectedMediaFiles function with this version
async function uploadSelectedMediaFiles() {
  // Helper to decide target CSV filename
  const csvName =
    (selectedQuiz && selectedQuiz.name) ||
    (currentCsvName ? (currentCsvName.endsWith(".csv") ? currentCsvName : `${currentCsvName}.csv`) : null);

  if (!mediaFiles || mediaFiles.length === 0 || uploading) {
    // Skip upload → still go straight to editor
    if (csvName) {
      window.location.href = `/csvquizedit?file=${encodeURIComponent(csvName)}`;
    } else {
      appAlert("No quiz file selected!");
    }
    return;
  }

  setUploading(true);
  setProgress(0);
  let completed = 0;

  try {
    for (const file of Array.from(mediaFiles)) {
      try {
        const safeName = cleanFilename(file.name);
        if (!safeName) continue;
        const storageRef = ref(storage, "media/" + safeName);
        await uploadBytes(storageRef, file);
      } catch (err) {
        console.error("Upload failed for", file.name, err);
      }
      completed += 1;
      setProgress(completed / mediaFiles.length);
    }
  } finally {
    setUploading(false);
  }

  // ✅ Redirect to editor for the CSV we know about
  if (csvName) {
    window.location.href = `/csvquizedit?file=${encodeURIComponent(csvName)}`;
  } else {
    appAlert("No quiz file selected!");
  }
}






// Full replacement: create game document id as
// "<QuizName>_<LoggedInUser>_<timestamp>"
// In Quizmaster.js, replace your entire handleHostQuizFile function with this one.

// Full replacement: create game document id as
// "<QuizName>_<LoggedInUser>_<timestamp>"
// In Quizmaster.js, replace the entire handleHostQuizFile function

const handleHostQuizFile = async (fileRef) => {
  const liveMode = isLiveHostMode();
  const tokenBalance = getQuizTokenBalance(userProfile);

  if (liveMode) {
    if (tokenBalance < 1) {
      const goPricing = await appConfirm(
        "You need at least 1 quiz token to host a LIVE quiz (Test Mode is Off).\n\n" +
        "Subscribe weekly or buy tokens on the Pricing page.\n\n" +
        "Tip: set Test Mode to On in Quiz Options to practise up to 5 questions for free.",
        { confirmLabel: "Open Pricing", cancelLabel: "Not now" }
      );
      if (goPricing) window.location.href = "/pricing";
      return;
    }
    const confirmed = await appConfirm(
      "This will start a LIVE hosted quiz (Test Mode is Off).\n\n" +
      "Starting spends 1 quiz token for this night.\n" +
      "If the connection drops, use Reconnect to resume the same quiz (no extra charge).\n\n" +
      `Current balance: ${tokenBalance} token(s).\n\n` +
      "Continue and spend 1 token?",
      { confirmLabel: "Spend 1 token & start", cancelLabel: "Cancel" }
    );
    if (!confirmed) return;
  }

  setHosting(true);
  setHostProgress(0);
  billingMaxQRef.current = 0;
  liveTokenSpentRef.current = false;

  // Helper to make a safe Firestore document id fragment
  const toSlug = (s) =>
    String(s || "")
      .replace(/\.csv$/i, "")     // drop .csv
      .trim()
      .replace(/\s+/g, "_")       // spaces -> underscore
      .replace(/[^A-Za-z0-9_]/g, "") // keep only letters/digits/underscore
      .slice(0, 64);              // be conservative on length

  try {
    const url = await getDownloadURL(fileRef);
    const res = await fetch(url);
    const csv = await res.text();

    const quizSlug = toSlug(fileRef?.name || "quiz");
    const userLabel =
      profileName ||
      auth.currentUser?.displayName ||
      auth.currentUser?.email ||
      auth.currentUser?.uid ||
      "user";
    const userSlug = toSlug(userLabel);

    const newGameCode = `${quizSlug}_${userSlug}_${Date.now()}`;
    setGameCode(newGameCode);
    testUsersAddedRef.current = false; // <-- RESET THE TRACKER FOR THE NEW GAME
    testLimitAlertShownRef.current = false;

    Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let qs = results.data;
        
        const endQuizIndex = qs.findIndex(q => (q.CAT || "").toUpperCase() === "END QUIZ");
        if (endQuizIndex !== -1) {
          qs = qs.slice(0, endQuizIndex + 1);
        }

        qs = qs.map((q) => {
            const catUpper = String(q.CAT || q.type || q.cat || q.TYPE || q.questionType || "").trim().toUpperCase();
            const isBreadcrumbs = catUpper === "BREADCRUMBS";
            const answers = isBreadcrumbs
              ? [q.A1].filter(Boolean)
              : [q.A1, q.A2, q.A3, q.A4, q.A5, q.A6].filter(Boolean);
            const rawAnswers = isBreadcrumbs ? [q.A1].filter(Boolean) : [q.A1, q.A2, q.A3, q.A4, q.A5, q.A6].filter(Boolean);
            const facts = isBreadcrumbs ? [q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11].filter(Boolean) : undefined;

            let maxSelectable = 1;
            if (catUpper === "MULTIANSWER") {
              maxSelectable = rawAnswers.filter((a) => /^\s*\[.*?\]\s*$/.test(a)).length;
            }

            const answersOrder = getShuffledOrder(answers.length);
            const rawTime = q.TIME !== undefined && q.TIME !== "" ? Number(q.TIME) : undefined;
            const time = !isNaN(rawTime) && rawTime !== null ? rawTime : settings.questionTimer || 20;

            const parsedPoints = Number(q.POINTS);
            const points = !isNaN(parsedPoints) ? parsedPoints : 0;

            return {
              question: q.Q,
              Q: q.Q,
              answers,
              ...(isBreadcrumbs ? { facts: facts || [] } : {}),
              type: q.CAT,
              CAT: q.CAT,
              sound: q.SOUND,
              video: q.VIDEO,
              background: q.BACKGROUND,
              p1: q.P1,
              answersOrder,
              maxSelectable,
              vanishOrder:
                catUpper === "VANISHINGCHOICE"
                  ? getRandomVanishOrder(answersOrder)
                  : null,
              time,
              TIME: time,
              points,
              POINTS: points,
              P1_URL: q.P1_URL || "",
              SOUND_URL: q.SOUND_URL || "",
              BACKGROUND_URL: q.BACKGROUND_URL || "",
              VIDEO_URL: q.VIDEO_URL || "",
              visibleExplanation: q.visibleExplanation || "",
              JIGSAW: q.JIGSAW || "",
            };
          });

        setHostProgress(1);
        setQuestions(qs);
        setStep(3);

        const joinCode = await generateNextJoinCode();

        await setDoc(doc(db, "games", newGameCode), {
          questions: qs,
          current: 0,
          timer: settings.questionTimer || 20,
          timerActive: false,
          state: "waiting",
          joinCode,
          title: fileRef?.name?.replace(/\.csv$/i, "") || "Quiz",
          hostName: userLabel,
          hostUid: auth.currentUser?.uid || null,
          createdAt: serverTimestamp(),
          settings: { ...quizOptions, ...teamRoundChoiceSettingsForGameDoc() },
          testMode: liveMode ? "Off" : "On",
        });

        if (liveMode) {
          try {
            await spendQuizToken({ gameId: newGameCode });
            liveTokenSpentRef.current = true;
            billingMaxQRef.current = 0;
          } catch (spendErr) {
            console.error("spendQuizToken failed:", spendErr);
            try { await deleteDoc(doc(db, "games", newGameCode)); } catch (_) {}
            setHosting(false);
            setStep(1);
            setGameCode("");
            const msg = String(spendErr?.message || spendErr);
            if (/NO_TOKENS|No quiz tokens|402/i.test(msg)) {
              const goPricing = await appConfirm(
                "You don't have enough quiz tokens to host live.\n\nOpen Pricing?",
                { confirmLabel: "Open Pricing", cancelLabel: "Not now" }
              );
              if (goPricing) window.location.href = "/pricing";
            } else {
              appAlert("Could not spend a quiz token: " + msg);
            }
            return;
          }
        }

        // --- THE TEST USER LOGIC IS NO LONGER HERE ---

        setHostProgress(1);
        setTimeout(() => setHosting(false), 300);
      },
    });
  } catch (err) {
    setHosting(false);
    appAlert("Error hosting quiz: " + err.message);
  }
};

const handleAppendQuestionsCSV = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setAppendingQuestions(true);

  Papa.parse(file, {
    // MODIFICATION: header is now false. This reads each row as an array of values.
    header: false, 
    skipEmptyLines: true,
    complete: async (results) => {
      const questionsToUpload = results.data;
      
      if (!questionsToUpload || questionsToUpload.length === 0) {
        appAlert("CSV is empty or could not be read.");
        setAppendingQuestions(false);
        return;
      }

        try {
                const batch = writeBatch(db);
                const questionsCollectionRef = collection(db, "QuestionsCollection");

                results.data.forEach((row) => {
                  if (row.length >= 4) {
                    const newQuestionDocRef = doc(questionsCollectionRef);
                    batch.set(newQuestionDocRef, {
                      question:     row[0], // Column 1: Question
                      qNumber:      row[1], // Column 2: Qnumber
                      questionType: row[2], // Column 3: Question Type
                      answer:       row[3], // Column 4: Answer
                      createdAt:    serverTimestamp()
                      // 'isEnriched' field is not needed; the query handles it
                    });
                  }
                });

                await batch.commit();
                appAlert(`Successfully uploaded ${results.data.length} questions! AI enrichment will begin automatically in the background.`);


      } catch (err) {
        console.error("Error appending questions: ", err);
        appAlert("An error occurred while uploading. Check the console for details.");
      } finally {
        setAppendingQuestions(false);
        e.target.value = null; 
      }
    },
    error: (err) => {
        appAlert(`Failed to parse CSV: ${err.message}`);
        setAppendingQuestions(false);
    }
  });
};


// ======================= ADD THIS ENTIRE NEW FUNCTION =======================
// ======================= REPLACE ENTIRE FUNCTION =======================
const handleLibraryUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setIsUploadingLibrary(true);
  setLibraryUploadProgress("");

  // The exact headers we expect in the CSV
  const LIBRARY_EXPECTED_HEADERS = [
    "Question","Genre","Answer","INTEREST_RATING","INTEREST_RATING_JUSTIFICATION",
    "Difficulty","Category","Flag","min_age","max_age","Demographic appeal","TAGS",
    "Geographic_Relevance","NOTES","Media","Time Sensitivity","Date Added",
    "Visible_explanation","A2","A3","A4","A5","A6","A7","A8","A9","A10","A11"
  ];

  // The minimal fields required for a valid row
  const LIBRARY_REQUIRED_ROW_FIELDS = ["Question", "Category", "Answer"];

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    transformHeader: h => String(h || "").trim(),
    complete: async (results) => {
      try {
        const rows = results.data || [];
        const csvHeaders = (results.meta?.fields || []).map(h => String(h || "").trim());

        // 1) Header validation
        const missingHeaders = LIBRARY_EXPECTED_HEADERS.filter(h => !csvHeaders.includes(h));
        if (missingHeaders.length) {
          appAlert(
            [
              "Upload to Library failed:",
              "",
              "Your CSV is missing the following headers:",
              "• " + missingHeaders.join(", "),
              "",
              "Expected all of these headers:",
              "• " + LIBRARY_EXPECTED_HEADERS.join(", "),
            ].join("\n")
          );
          return;
        }

        // 2) Row validation
        const invalidRows = [];
        const isEmptyRow = (row) =>
          LIBRARY_EXPECTED_HEADERS.every(h => {
            const v = row[h];
            return v == null || String(v).trim() === "";
          });

        rows.forEach((row, i) => {
          if (isEmptyRow(row)) return; // ignore fully blank lines
          const missingReq = LIBRARY_REQUIRED_ROW_FIELDS.filter(f => {
            const v = row[f];
            return v == null || String(v).trim() === "";
          });
          if (missingReq.length) {
            invalidRows.push({ idx: i + 2 /* header is line 1 */, missing: missingReq });
          }
        });

        if (invalidRows.length) {
          const preview = invalidRows.slice(0, 10).map(r =>
            `Row ${r.idx}: missing ${r.missing.join(", ")}`
          );
          appAlert(
            [
              "Upload to Library cancelled:",
              "Some rows are missing required fields.",
              "",
              `Required per row: ${LIBRARY_REQUIRED_ROW_FIELDS.join(", ")}`,
              "",
              ...preview,
              invalidRows.length > 10
                ? `…and ${invalidRows.length - 10} more row(s).`
                : "",
            ].join("\n")
          );
          return;
        }

        // 3) Build batch with safe mapping (same as before) but only for non-empty rows
        const batch = writeBatch(db);
        const libraryCollectionRef = collection(db, "QuestionLibrary");
        const validRows = rows.filter(r => !isEmptyRow(r));
        const totalRows = validRows.length;
        const normalizeQuestion = (value) =>
          String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

        // Preload existing questions for duplicate checking (question text only)
        setLibraryUploadProgress("Checking duplicates...");
        const existingSnap = await getDocs(libraryCollectionRef);
        const existingQuestions = new Set();
        existingSnap.forEach((docSnap) => {
          const qText = docSnap.data()?.question;
          const normalized = normalizeQuestion(qText);
          if (normalized) existingQuestions.add(normalized);
        });

        const seenInCsv = new Set();
        const skippedDuplicates = [];
        let uploadedCount = 0;

        for (let i = 0; i < validRows.length; i++) {
          const row = validRows[i];
          const rawQuestion = row["Question"];
          const normalizedQuestion = normalizeQuestion(rawQuestion);

          if (normalizedQuestion && existingQuestions.has(normalizedQuestion)) {
            skippedDuplicates.push({ question: rawQuestion || "(blank)", reason: "Already in library" });
            continue;
          }
          if (normalizedQuestion && seenInCsv.has(normalizedQuestion)) {
            skippedDuplicates.push({ question: rawQuestion || "(blank)", reason: "Duplicate in CSV" });
            continue;
          }
          if (normalizedQuestion) seenInCsv.add(normalizedQuestion);

          setLibraryUploadProgress(`${i + 1}/${totalRows}`);

          // Map fields from the CSV to Firestore doc
          const docData = {
            question: row["Question"] || "",
            answer: row["Answer"] || "",
            interestRating: Number.parseInt(row["INTEREST_RATING"], 10) || null,
            interestRatingJustification: row["INTEREST_RATING_JUSTIFICATION"] || "",
            difficulty: Number.parseInt(row["Difficulty"], 10) || null,
            questionType: row["Category"] || "",
            genre: row["Genre"] || "",
            minAge: Number.parseInt(row["min_age"], 10) || null,
            maxAge: Number.parseInt(row["max_age"], 10) || null,
            notes: row["NOTES"] || "",
            visibleExplanation: row["Visible_explanation"] || "",
            mediaSuggestion: row["Media"] || "",
            timeSensitivityYear: Number.parseInt(row["Time Sensitivity"], 10) || null,
            accuracyFlag:
              row["Flag"] == null || String(row["Flag"]).trim() === ""
                ? ""
                : String(row["Flag"]).trim(),

            A2: row["A2"] || null,
            A3: row["A3"] || null,
            A4: row["A4"] || null,
            A5: row["A5"] || null,
            A6: row["A6"] || null,
            A7: row["A7"] || null,
            A8: row["A8"] || null,
            A9: row["A9"] || null,
            A10: row["A10"] || null,
            A11: row["A11"] || null,
            tags: row["TAGS"] ? String(row["TAGS"]).split(",").map(s => s.trim()).filter(Boolean) : [],
            demographicAppeal: row["Demographic appeal"] ? String(row["Demographic appeal"]).split(",").map(s => s.trim()).filter(Boolean) : [],
            geographicRelevance: row["Geographic_Relevance"] ? String(row["Geographic_Relevance"]).split(",").map(s => s.trim()).filter(Boolean) : [],
            dateAdded: row["Date Added"] || null,
            uploadedAt: serverTimestamp(),
          };

          const newDocRef = doc(libraryCollectionRef);
          batch.set(newDocRef, docData);
          uploadedCount += 1;
        }

        if (!totalRows) {
          appAlert("Upload to Library cancelled: no valid (non-empty) rows found.");
          return;
        }
        if (!uploadedCount) {
          appAlert(`Upload to Library cancelled: all ${totalRows} row(s) were duplicates.`);
          return;
        }

        await batch.commit();
        if (skippedDuplicates.length) {
          const preview = skippedDuplicates.slice(0, 15).map(d =>
            `• ${d.question} (${d.reason})`
          );
          appAlert(
            [
              `Successfully uploaded ${uploadedCount} question(s) to the Question Library.`,
              "",
              `Skipped ${skippedDuplicates.length} duplicate question(s):`,
              ...preview,
              skippedDuplicates.length > 15
                ? `…and ${skippedDuplicates.length - 15} more.`
                : ""
            ].join("\n")
          );
        } else {
          appAlert(`Successfully uploaded ${uploadedCount} questions to the Question Library!`);
        }
      } catch (err) {
        console.error("Error uploading to library: ", err);
        appAlert("An error occurred during the library upload. Check the console for details.");
      } finally {
        setIsUploadingLibrary(false);
        setLibraryUploadProgress("");
        e.target.value = null;
      }
    },
    error: (err) => {
      console.error("CSV parse error:", err);
      appAlert(`Failed to parse CSV: ${err?.message || err}`);
      setIsUploadingLibrary(false);
      setLibraryUploadProgress("");
      e.target.value = null;
    }
  });
};


  // ---- CSV Handling ----
// FULL REPLACEMENT: handleCSV
const handleCSV = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setCurrentCsvName(file.name);

  // Upload to Storage
  const storageRef = ref(storage, `quizzes/${file.name}`);
  await uploadBytes(storageRef, file);

  // Tag owner in Storage metadata (as you already do)
  const ownerUid = auth.currentUser?.uid || null;
  if (ownerUid) {
    await updateMetadata(storageRef, { customMetadata: { ownerUid } });
  }

  // NEW: make/merge Firestore index doc so listing & sharing work
  await setDoc(
    doc(db, "quizzes", file.name),
    {
      ownerUid,
      storagePath: `quizzes/${file.name}`,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }   // never wipes existing sharedWithUids
  );

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: (results) => {
      let qs = results.data.map((q) => {
        const filled = {};
        for (const field of REQUIRED_FIELDS) filled[field] = q[field] ?? "";

        const catUpper = String(filled.CAT || q.CAT || filled.type || q.type || "").trim().toUpperCase();
        const isBreadcrumbs = catUpper === "BREADCRUMBS";
        const rawAnswers = isBreadcrumbs
          ? [q.A1].filter((a) => !!a)
          : [filled.A1, filled.A2, filled.A3, filled.A4, filled.A5, filled.A6].filter((a) => !!a);
        const facts = isBreadcrumbs ? [q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11].filter((a) => !!a) : undefined;

        let maxSelectable = 1;
        if (catUpper === "MULTIANSWER") {
          maxSelectable = rawAnswers.filter((a) => /^\s*\[.*?\]\s*$/.test(a)).length;
        }

        const answers = rawAnswers;
        const answersOrder = getShuffledOrder(answers.length);
        const rawTime = typeof filled.TIME !== "undefined" && filled.TIME !== ""
          ? Number(filled.TIME) : undefined;

        const time = !isNaN(rawTime) && rawTime !== null
          ? rawTime
          : settings.questionTimer || 20;

        return {
          ...q,  // Spread all CSV fields first to preserve all columns
          question: filled.Q,
          Q: filled.Q,
          answers,
          ...(isBreadcrumbs ? { facts: facts || [] } : {}),
          type: filled.CAT,
          CAT: filled.CAT,
          sound: filled.SOUND,
          video: filled.VIDEO,
          background: filled.BACKGROUND,
          p1: filled.P1,
          answersOrder,
          maxSelectable,
          vanishOrder:
            catUpper === "VANISHINGCHOICE"
              ? getRandomVanishOrder(answersOrder)
              : undefined,
          time,
          TIME: time,
          visibleExplanation: q.visibleExplanation || "",  // Explicitly preserve visibleExplanation from CSV
          JIGSAW: q.JIGSAW || "",
        };
      });

      qs = qs.filter(q => !!q.question);
      setQuestions(qs);

      const needed = getAllMediaFilenames(qs);
      setMediaNeeded(needed);

      if (needed.length) {
        setStep(2);
      } else {
        const csvName = file.name.endsWith(".csv") ? file.name : `${file.name}.csv`;
        window.location.href = `/csvquizedit?file=${encodeURIComponent(csvName)}`;
      }
    }
  });
};



  // ---- Media upload handling ----
  const handleMediaFiles = (e) => {
    const files = e.target.files;
    setMediaFiles(files);
    // Check for any missing files (by name)
    const missing = mediaNeeded.filter((fname) => !findFile(files, fname));
    setMissingFiles(missing);
  };

const handleUploadAll = async () => {
  let qs = await uploadAndReplaceMedia(questions, mediaFiles, setProgress);

  // DO NOT alert if files are missing. Allow upload anyway.
  try {
    setQuestions(qs);
    await setDoc(doc(db, "games", gameCode), {
      questions: qs,
      current: 0,
      state: "waiting",
    });
    setStep(1);
    // Immediately redirect home, no popups.
    window.location.href = "/host";
  } catch (err) {
    console.error("Error during upload or saving quiz:", err);
    appAlert("Error uploading files or saving quiz: " + err.message);
  }
};


// In Quizmaster.js, REPLACE your existing `advance` function with this one:

// In Quizmaster.js, replace the entire advance function

// In Quizmaster.js, replace the entire `advance` function

// Quizmaster.js (REPLACE ENTIRE EXISTING advance FUNCTION, starting around line 1243)









useEffect(() => {
const handleKeyDown = (e) => {
  const t = e.target;
  const tag = (t && t.tagName) ? t.tagName.toUpperCase() : "";
  const inEditable =
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    (t && (t.isContentEditable || t.getAttribute?.("contenteditable") === "true"));

  if (inEditable) return;
  if (e.ctrlKey || e.metaKey || e.altKey) return;

  const k = (e.key || "").toLowerCase();
  const keyCode = e.keyCode;
  // Fire Stick remote: Play/Pause=179; Fast Forward=228 or 90 (Android); Rewind=227 or 89 (Android); D-pad Left=37, Right=39
  // Also check key/code for MediaTrackNext/MediaTrackPrevious (browser may send these when keyCode is 0 or 229)
  const isPlayPause = k === "p" || k === "mediaplaypause" || e.key === "MediaPlayPause" || keyCode === 179;
  const code = (e.code || "").toLowerCase();
  const isNext = e.key === "ArrowRight" || keyCode === 228 || keyCode === 90 || keyCode === 39 ||
    e.key === "MediaTrackNext" || code === "mediatracknext" ||
    e.key === "MediaFastForward" || code === "mediafastforward";
  const isPrev = e.key === "ArrowLeft" || keyCode === 227 || keyCode === 89 || keyCode === 37 ||
    e.key === "MediaTrackPrevious" || code === "mediatrackprevious" ||
    e.key === "MediaRewind" || code === "mediarewind";

  if (k === "q") { setTab("questions"); return; }
  if (k === "s") { setTab("scores"); return; }

  // Host keyboard: 'o' -> Options popup is now handled locally on display side only (no Firebase)
  // The display screen handles "O" key and ☰ (Menu) via local state toggle
  if (k === "o") {
    return;
  }

  // Pause/unpause auto-advance (keyboard P or remote Play/Pause); when pausing show options popup, when resuming hide it
  if (isPlayPause) {
    e.preventDefault();
    setAutoPaused((prev) => {
      const nextPaused = !prev;
      setShowQuizOptions(nextPaused); // paused -> show options, playing -> hide options

      // When unpausing, check if there's a pending JACKPOT question to write
      if (prev && !nextPaused && pendingJackpotRef.current) {
        const pending = pendingJackpotRef.current;
        // Write pending JACKPOT question to game document
        (async () => {
          try {
            const gameDocRef = doc(db, "games", gameCode);
            const snap = await getDoc(gameDocRef);
            const g = snap.data() || {};
            const nextVersion = (g.questionVersion || 0) + 1;
            let questionsArray = [];
            if (Array.isArray(g.questions)) {
              questionsArray = [...g.questions];
            } else if (g.questions) {
              try { questionsArray = Object.values(g.questions); } catch { questionsArray = []; }
            }
            while (questionsArray.length <= pending.targetIdx) questionsArray.push({});
            questionsArray[pending.targetIdx] = pending.questionData;
            const jackpotSec = pending.questionData.TIME || pending.questionData.time || 0;
            const baseUpdatePayload = {
              current: pending.targetIdx,
              autoPaused: false,
              opt_autoplay: true,
            };
            applyAdvanceTimerFields(pending.questionData, jackpotSec, baseUpdatePayload);
            // Prepare update payload
            const jackpotUpdatePayload = {
              ...baseUpdatePayload,
              questionVersion: nextVersion,
              currentQuestion: pending.questionData,
              questions: questionsArray
            };

            // In test mode, add the JACKPOT question ID to testModeUsedQuestionIds
            const isTestMode = localStorage.getItem("opt_test_mode") === "On";
            if (isTestMode && pending.questionId) {
              jackpotUpdatePayload.testModeUsedQuestionIds = arrayUnion(pending.questionId);
            }

            await updateDoc(gameDocRef, jackpotUpdatePayload);

            // Record usage history
            try {
              const usageEntry = {
                usedAt: new Date(),
                gameId: gameCode || "unknown_game",
                hostId: auth.currentUser?.uid || "unknown_host",
                location: { city: "Brisbane", state: "QLD", country: "AU", venueId: pending.currentVenue }
              };
              const questionDocRef = doc(db, "QuestionLibrary", pending.questionId);
              await updateDoc(questionDocRef, { usageHistory: arrayUnion(usageEntry) });
            } catch {}
            
            // Clear pending ref
            pendingJackpotRef.current = null;
            console.log("JACKPOT question written to game document after unpause.");
          } catch (err) {
            console.error("Error writing pending JACKPOT question:", err);
          }
        })();
      }

      // When paused => opt_autoplay = "No"; when resumed => "Yes"
      setOptAutoplayLocal(!nextPaused);

      // Blow away any scheduled auto-advance immediately (legacy timer)
      if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = 0; }

      // Clear the global auto-advance deadline used by useSlideTiming and AutoAdvancePump
      if (typeof window !== "undefined") {
        window.__autoAdvanceAtMs = 0;
        window.__autoAdvanceReason = "";
      }

      // (Optional) also write a hint into the game doc so other clients can listen if you wire it up later:
      try {
        if (gameCode) {
          updateDoc(doc(db, "games", gameCode), {
            autoPaused: nextPaused,
            opt_autoplay: !nextPaused
          }).catch(() => {});
        }
      } catch { /* ignore */ }

      return nextPaused;
    });
    return;
  }

  // Next/Prev (FF/Rewind) work globally so they're not blocked by tab state
  if (isNext) {
    e.preventDefault();
    keyupIgnoreNextPrevUntilRef.current = Date.now() + 400;
    try { window.__skipWheelTransitionOnce = true; } catch {}
    advance(1);
    return;
  }
  if (isPrev) {
    e.preventDefault();
    keyupIgnoreNextPrevUntilRef.current = Date.now() + 400;
    try { window.__skipWheelTransitionOnce = true; } catch {}
    advance(-1);
    return;
  }

  if (tab !== "questions") return;

  if (k === "r") {
    e.preventDefault();
    if (current !== 0) setCurrent(0);
  }
};



  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, [current, questions.length, tab, resumeQuestionIdx, gameCode]);

// keyup: same next/prev logic so Fire TV remotes that only fire keyup still work; skip if we already handled this press on keydown (works globally, no tab check)
useEffect(() => {
  const handleKeyUp = (e) => {
    if (Date.now() < keyupIgnoreNextPrevUntilRef.current) return;
    const t = e.target;
    const tag = (t && t.tagName) ? t.tagName.toUpperCase() : "";
    const inEditable =
      tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
      (t && (t.isContentEditable || t.getAttribute?.("contenteditable") === "true"));
    if (inEditable) return;
    const keyCode = e.keyCode;
    const code = (e.code || "").toLowerCase();
    const isNext = e.key === "ArrowRight" || keyCode === 228 || keyCode === 90 || keyCode === 39 ||
      e.key === "MediaTrackNext" || code === "mediatracknext" ||
      e.key === "MediaFastForward" || code === "mediafastforward";
    const isPrev = e.key === "ArrowLeft" || keyCode === 227 || keyCode === 89 || keyCode === 37 ||
      e.key === "MediaTrackPrevious" || code === "mediatrackprevious" ||
      e.key === "MediaRewind" || code === "mediarewind";
    if (isNext) {
      e.preventDefault();
      keyupIgnoreNextPrevUntilRef.current = Date.now() + 400;
      try { window.__skipWheelTransitionOnce = true; } catch {}
      advance(1);
      return;
    }
    if (isPrev) {
      e.preventDefault();
      keyupIgnoreNextPrevUntilRef.current = Date.now() + 400;
      try { window.__skipWheelTransitionOnce = true; } catch {}
      advance(-1);
    }
  };
  window.addEventListener("keyup", handleKeyUp);
  return () => window.removeEventListener("keyup", handleKeyUp);
}, [current, questions.length, tab, resumeQuestionIdx, gameCode]);

// Media Session API: route hardware next/previous and seek (FF/Rewind) to advance when quiz is live
useEffect(() => {
  if (step !== 3 || typeof navigator === "undefined" || !navigator.mediaSession) return;
  navigator.mediaSession.playbackState = "playing";
  const handleNext = () => {
    if (advanceRef.current) {
      try { window.__skipWheelTransitionOnce = true; } catch {}
      advanceRef.current(1);
    }
  };
  const handlePrev = () => {
    if (advanceRef.current) {
      try { window.__skipWheelTransitionOnce = true; } catch {}
      advanceRef.current(-1);
    }
  };
  navigator.mediaSession.setActionHandler("nexttrack", handleNext);
  navigator.mediaSession.setActionHandler("previoustrack", handlePrev);
  navigator.mediaSession.setActionHandler("seekforward", handleNext);
  navigator.mediaSession.setActionHandler("seekbackward", handlePrev);
  return () => {
    try {
      navigator.mediaSession.setActionHandler("nexttrack", null);
      navigator.mediaSession.setActionHandler("previoustrack", null);
      navigator.mediaSession.setActionHandler("seekforward", null);
      navigator.mediaSession.setActionHandler("seekbackward", null);
    } catch (_) {}
  };
}, [step]);

  if (checking) return null;
  if (!user) return <QuizmasterLogin onLogin={() => setUser(auth.currentUser)} />;

  // ---- UI Step 0: Quiz Options ----
if (step === 0) {
    return (
        <div style={{
            minHeight: "100vh", width: "100vw",
            background: `url(${bgUrl}) center center / cover no-repeat`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
        }}>
            <div style={{
                background: "rgba(28, 34, 58, 0.85)", backdropFilter: "blur(10px)",
                borderRadius: "2em", padding: "2em 3em",
                boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
                width: "min(800px, 90vw)", color: "#fff",
            }}>
                <h1 style={{
                    textAlign: "center", fontSize: "2.5rem", fontWeight: 900,
                    color: "#ffe066", textShadow: "2px 2px 8px #222, 0 2px 12px #2ae",
                    marginBottom: "1em",
                }}>
                    Quiz Options
                </h1>
                <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: "1.5em 2em", marginBottom: "2em",
                }}>
                    {/* Render 10 options here */}
                    {Object.entries({
                        'AI Host': ['None', 'Ollie'],
                        'Player Join Time': ['60s', '90s', '120s', 'Unlimited'],
                        'Enable Team Selfies': ['On', 'Off'],
                        'Score Display': ['After Each Question', 'End of Round'],
                        'Background Music Volume': 'slider',
                        'Penalty for Incorrect Answers': ['On', 'Off'],
                        'Tie-Breaker Mode': ['Fastest Answer Wins', 'Manual'],
                        'Show Top Teams Between Rounds': ['On', 'Off'],
                        'Player Name Filter': ['On', 'Off'],
                        'Allow Players to Rejoin': ['On', 'Off'],
                    }).map(([label, options]) => (
                        <div key={label}>
                            <label style={{ display: "block", marginBottom: "0.5em", fontWeight: 700 }}>{label}</label>
                            {options === 'slider' ? (
                                <input type="range" min="0" max="100" />
                            ) : (
                                <select style={{width: "100%", padding: "0.5em", borderRadius: "8px", border: "none"}}>
                                    {options.map(o => <option key={o}>{o}</option>)}
                                </select>
                            )}
                        </div>
                    ))}
                </div>
                <div style={{ textAlign: "center" }}>
                    <button onClick={() => setStep(1)} style={{
                        background: "#47e373", color: "#111", fontWeight: 900,
                        border: "none", borderRadius: "1em", padding: "0.8em 3em",
                        fontSize: "1.2rem", cursor: "pointer",
                    }}>
                        Start Quiz
                    </button>
                </div>
            </div>
        </div>
    );
}


  // ---- UI Step 1: Select/Upload Quiz ----
if (step === 1) {
  return (
  <div
    style={{
      minHeight: "100vh",
      width: "100vw",
      margin: 0,
      padding: 0,
      overflow: "hidden",
      background: bgUrl
        ? `url(${bgUrl}) center center / cover no-repeat`
        : "#223b4c",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      fontFamily: "inherit",
    }}
  >
{/* Top bar — Menu + user name on the left */}
<div
  style={{
    width: "100vw",
    padding: "14px 16px",
    background: "rgba(20,15,44,0.55)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    position: "sticky",
    top: 0,
    zIndex: 999,
  }}
>
  <button
    aria-label="Open menu"
    title="Menu"
    onClick={() => setShowMenu(true)}
    style={{
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.16)",
      color: "#ffe066",
      fontWeight: 900,
      borderRadius: 12,
      padding: "10px 14px",
      cursor: "pointer",
    }}
  >
    ☰ Menu
  </button>

  {/* User name chip now sits right next to the Menu button */}
  {profileName ? (
    <span
      title={user?.email || ""}
      style={{
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.25)",
        color: "#fff",
        fontWeight: 800,
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: "0.9em",
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
      }}
    >
      {profileName}
    </span>
  ) : null}



  {/* spacer for any future right-side items */}
  <div style={{ marginLeft: "auto" }} />
</div>


{/* Sidebar instance */}
<Menu
  open={showMenu}
  onClose={() => setShowMenu(false)}
  onReconnect={() => setShowReconnect?.(true)} // keep your existing reconnect behaviour
  
/>







<div style={{ width: "100%", maxWidth: "600px", margin: "0 auto 32px auto" }}>
  {loadingQuizzes ? (
    <div style={{
      color: "#fff",
      fontSize: "1.2em",
      margin: 20,
      fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
      fontWeight: 700,
      textShadow: "1.5px 1.5px 6px #2ae, 0 1.5px 5px #222"
    }}>
      Loading quizzes...
    </div>
  ) : (
   <div style={{
  // Leave room for the top bar and the (possibly two-row) button bar so Host Quiz is always on screen.
  maxHeight: "min(80vh, calc(100vh - 340px))",
  minHeight: "200px",
  overflowY: "auto",
  //margin: "0 0 2vh 0",         // Add space at bottom for buttons
  width: "100%",
  maxWidth: 800,
  background: "rgba(28,34,58,0.13)",
  borderRadius: "1.5em",
  padding: "4px 0 4px 0",
  boxShadow: "0 8px 24px 0 #0003"
}}>
{/* Table header — same columns as each row below (no grid lines) */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: QUIZ_LIST_COLUMNS,
    gap: 10,
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 2,
    padding: "10px 15px 8px", // matches row padding (.7em at row font size) + 2.5px border
    marginBottom: 6,
    background: "rgba(20,15,44,0.85)",
    borderRadius: "1.2em 1.2em 0 0",
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    fontWeight: 900,
    fontSize: "0.92em",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#ffe066",
    textShadow: "1.5px 1.5px 6px #2ae, 0 1.5px 5px #222",
  }}
>
  <div>Quiz name</div>
  <div style={{ textAlign: "center" }}>Owner</div>
  <div style={{ textAlign: "center" }}>Questions</div>
</div>
<ul style={{ listStyle: "none", padding: 0, margin: 0, width: "100%" }}>
  {quizFiles.map((fileObj) => {
    const isSelected = selectedQuiz && selectedQuiz.name === fileObj.name;

    const dateStr =
      typeof fileObj.updated === "number" && !isNaN(fileObj.updated)
        ? new Date(fileObj.updated).toLocaleString()
        : "—";

    const questionCount = quizQuestionCounts[fileObj.name];

    return (
      <li
        key={fileObj.name}
        onClick={() => setSelectedQuiz(fileObj)}
        style={{
          background: isSelected ? "rgba(255,255,255,0.18)" : "rgba(35,35,61,0.23)",
          margin: "0 0 15px 0",
          borderRadius: "1.2em",
          padding: ".1em .7em .6em",
          fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
          fontWeight: 900,
          color: "#f6fcff",
          fontSize: "1.09em",
          cursor: "pointer",
          border: isSelected ? "2.5px solid #ffd700" : "2.5px solid #444c",
          boxShadow: isSelected ? "0 4px 16px 0 #ffd70022, 0 0 0 5px #ffe06633" : "0 2.5px 10px 0 #16162233",
          transition: "background .17s, border .18s, box-shadow .15s",
          textShadow: "1.5px 1.5px 6px #2ae, 0 1.5px 5px #222"
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: QUIZ_LIST_COLUMNS, alignItems: "center", gap: 10 }}>
          <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {fileObj.name.replace(/\.csv$/i, "")}
            <div style={{ fontSize: "0.95em", color: "#b5b8c6", fontWeight: 400, marginTop: 4 }}>
              Last updated: {dateStr}
            </div>
          </div>

          {/* Owner tag (now uses displayName) */}
          <span
            style={{
              justifySelf: "center",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 700,
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: "0.82em",
              letterSpacing: 0.2
            }}
            title={fileObj.ownerUid || ""}
          >
            {fileObj.ownerName || "unknown"}
          </span>

          {/* Number of playable questions in the quiz */}
          <span
            style={{
              justifySelf: "center",
              minWidth: 44,
              textAlign: "center",
              background: "rgba(255,224,102,0.16)",
              border: "1px solid rgba(255,224,102,0.45)",
              color: "#ffe066",
              fontWeight: 700,
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: "0.82em",
              letterSpacing: 0.2
            }}
            title="Questions in this quiz (not counting join, round and score slides)"
          >
            {questionCount === undefined ? "…" : questionCount}
          </span>
        </div>
      </li>
    );
  })}
</ul>



    </div>
  )}


{/* Action bar for selected quiz */}
{(() => {
  // Owner = the selected quiz's ownerUid matches the logged-in user's uid
  // (fallback to ownerName === "You" if present)
  const isOwner =
    !!selectedQuiz &&
    ((selectedQuiz.ownerUid && user?.uid && selectedQuiz.ownerUid === user.uid) ||
      selectedQuiz.ownerName === "You");

  const canHost   = !!selectedQuiz && !hosting;
  const canEdit   = !!selectedQuiz && !hosting;   // edit allowed (save is gated in editor)
  const canDelete = !!selectedQuiz && !hosting && isOwner;
  const canShare  = !!selectedQuiz && isOwner;

// Hardcoded Qwizard check
  const isQwizard =
    userProfile?.role === 'Qwizard' ||
    user?.email === 'rob@izeus.org' ||
    user?.email === 'suelaw42@gmail.com';

   // const canManage =
   // !!user && (user.uid === ownerUid || claims?.role === "Qwizard");

  //const isQwizard = true;

  return (
    <div
      style={{
        margin: "30px 0 10px 0",
        display: "flex",
        flexWrap: "wrap", // narrow windows: wrap instead of pushing Host Quiz off the left edge
        justifyContent: "center",
        gap: "10px 12px",
        padding: "0 12px",
        maxWidth: "100vw",
      }}
    >
      {/* Host Quiz */}
      <button
        className="qm-action-btn"
        style={quizActionButtonStyle("host", canHost)}
        onClick={() => canHost && handleHostQuizFile(selectedQuiz.ref)}
        disabled={!canHost}
      >
        {hosting
          ? `Loading... ${
              hostProgress > 0 && hostProgress < 1
                ? Math.round(hostProgress * 100) + "%"
                : ""
            }`
          : "▶  Host Quiz"}
      </button>

      {/* Edit Quiz (still allowed; save is locked in the editor for non-owners) */}
      <button
        className="qm-action-btn"
        style={quizActionButtonStyle("edit", canEdit)}
        onClick={() => {
          if (!canEdit) return;
          window.location.href = `/csvquizedit?file=${encodeURIComponent(
            selectedQuiz.name
          )}`;
        }}
        disabled={!canEdit}
      >
        ✎  Edit Quiz
      </button>

      {/* Delete Quiz — disabled when not owner */}
     <button
        className="qm-action-btn"
        style={quizActionButtonStyle("delete", canDelete)}
  onClick={async () => {
    if (!canDelete) return;
    if (
      !(await appConfirm(
        `Delete "${selectedQuiz.name.replace(/\.csv$/i, "")}"?\n\nThis deletes the quiz file and its share settings. This cannot be undone.`,
        { confirmLabel: "Delete quiz", cancelLabel: "Keep it", danger: true }
      ))
    ) {
      return;
    }

    try {
      const fileName = selectedQuiz.name;

      // --- Firestore: delete /quizzes/<fileName>/shares/* and the quiz doc itself
      try {
        const quizDocRef = doc(db, "quizzes", fileName);
        const sharesColRef = collection(quizDocRef, "shares");
        const sharesSnap = await getDocs(sharesColRef);

        const batch = writeBatch(db);
        sharesSnap.forEach((s) =>
          batch.delete(doc(quizDocRef, "shares", s.id))
        );
        batch.delete(quizDocRef); // delete the parent quiz doc
        await batch.commit();
      } catch (e) {
        console.warn("Firestore delete skipped/failed:", e?.message || e);
      }

      // --- Storage: delete the CSV object (ok if already gone)
      try {
        await deleteObject(selectedQuiz.ref);
      } catch (e) {
        console.warn("Storage delete skipped/failed:", e?.message || e);
      }

      // --- UI: remove from list and clear selection
      setQuizFiles((prev) => prev.filter((f) => f.name !== fileName));
      setSelectedQuiz(null);
    } catch (err) {
      appAlert("Could not delete quiz: " + (err?.message || err));
    }
  }}
  disabled={!canDelete}
>
  ✕  Delete Quiz
</button>

      {/* Upload CSV (unchanged) */}
      <button
        className="qm-action-btn"
        style={quizActionButtonStyle("upload", !hosting)}
        onClick={() =>
          !hosting && document.getElementById("quizUploadAction").click()
        }
        disabled={hosting}
      >
        ⬆  Upload CSV
      </button>

      {/* Share Quiz — disabled when not owner */}
      <button
        className="qm-action-btn"
        style={quizActionButtonStyle("share", canShare)}
        onClick={() => {
          if (!canShare) return;
          window.location.href = `/share?file=${encodeURIComponent(
            selectedQuiz.name
          )}`;
        }}
        disabled={!canShare}
      >
        ↗  Share Quiz
      </button>

      {/* New Quiz */}
      <button
        className="qm-action-btn"
        style={quizActionButtonStyle("newQuiz", true)}
        onClick={() => {
          window.location.href = "/csvquizedit";
        }}
      >
        ＋  New Quiz
      </button>




         {isQwizard && (
        <button
        className="qm-action-btn"
        style={quizActionButtonStyle("questions", !appendingQuestions)}
          onClick={() => document.getElementById("questionsAppendUpload").click()}
          disabled={appendingQuestions}
        >
          {appendingQuestions ? "Uploading..." : "⬆  Upload Questions (Disabled)"}
        </button>
      )}



{isQwizard && (
        <button
        className="qm-action-btn"
        style={quizActionButtonStyle("library", !isUploadingLibrary)}
          onClick={() => document.getElementById('libraryUploadInput').click()}
          disabled={isUploadingLibrary}
        >
          {isUploadingLibrary ? `Uploading... ${libraryUploadProgress}` : "⬆  Upload to Library"}
        </button>
      )}

{/* ADD THIS NEW HIDDEN INPUT at the end of the button container */}
      <input
        type="file"
        id="libraryUploadInput"
        accept=".csv"
        onChange={handleLibraryUpload}
        style={{ display: 'none' }}
        disabled={isUploadingLibrary}
      />


{/* ===================== ADD THIS NEW HIDDEN INPUT ===================== */}
      <input
        type="file"
        id="questionsAppendUpload"
        accept=".csv"
        onChange={handleAppendQuestionsCSV}
        style={{ display: "none" }}
        disabled={appendingQuestions}
      />
      {/* ======================= END OF NEW HIDDEN INPUT ===================== */}

      {/* hidden file input remains unchanged */}
      <input
        type="file"
        id="quizUploadAction"
        accept=".csv"
        onChange={handleCSV}
        style={{ display: "none" }}
        disabled={hosting}
      />
    </div>
  );
})()}


</div>

{/* CSV upload (unchanged, but styled to match) */}

<Reconnect
  open={showReconnect}
  onClose={() => setShowReconnect(false)}
  onReconnect={g => {
    setGameCode(g.id);
    setQuestions(g.questions || []);
    setCurrent(typeof g.current === "number" ? g.current : 0);
    setSettings(g.settings || {});
    liveTokenSpentRef.current = !!(g.billing && g.billing.tokenSpent && !g.billing.tokenRefunded);
    billingMaxQRef.current = Number(g.billing?.maxQuestionIndex) || Number(g.current) || 0;
    setStep(3);
    setIsHost(true);
    setEditMode(false);
  }}
/>

  </div>
);
}
 if (step === 2) {
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        background: bgUrl
          ? `linear-gradient(rgba(24,24,40,0.68), rgba(44,16,50,0.74)), url(${bgUrl}) center center / cover no-repeat`
          : "#223b4c",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "rgba(32,30,48,0.58)",
          boxShadow: "0 8px 36px 8px #1116",
          borderRadius: "2.4em",
          padding: "2.2em 2.5em 2.2em 2.5em",
          maxWidth: 560,
          width: "98vw",
          minHeight: 380,
          backdropFilter: "blur(12px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "#f8faff",
        }}
      >
        <h2
          style={{
            margin: "0 0 1.2em 0",
            fontSize: "2.0rem",
            fontWeight: 900,
            color: "#fff",
            letterSpacing: 0.5,
            textShadow: "2px 2px 8px #222, 0 2px 12px #2ae",
            textAlign: "center",
          }}
        >
          Upload Media Files
        </h2>
        <div style={{ color: "#e5e9ee", fontWeight: 700, marginBottom: 12 }}>
          Select any files you want to upload (audio, images, etc).
        </div>
        <input
          type="file"
          accept=".mp3,.wav,.jpg,.jpeg,.png,.gif,.mp4,.wmv"
          multiple
          disabled={uploading}
          onChange={e => setMediaFiles(e.target.files)}
          style={{ marginBottom: 18, fontSize: "1.06em" }}
        />
        <div
          style={{
            marginTop: 12,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
       <button
  style={{
    padding: "0.85em 2.1em",
    background: "linear-gradient(135deg, rgba(255,204,102,0.80), rgba(204,102,0,0.93))",
    color: "#222",
    fontWeight: 900,
    borderRadius: "2em",
    cursor: uploading || mediaFiles.length === 0 ? "not-allowed" : "pointer",
    boxShadow: "inset 0 2px 8px rgba(255,255,255,0.16), 0 4px 14px #0003",
    fontSize: "1.08rem",
    letterSpacing: 0.1,
    textShadow: "1.5px 1.5px 5px #fff8, 0 1.5px 5px #222",
    border: "none",
    marginRight: 10,
    opacity: uploading || mediaFiles.length === 0 ? 0.6 : 1,
    pointerEvents: uploading || mediaFiles.length === 0 ? "none" : "auto",
  }}
  onClick={uploadSelectedMediaFiles}
  disabled={uploading || mediaFiles.length === 0}
>
  {uploading ? "Uploading..." : "Upload Files"}
</button>
          <button
            style={{
              padding: "0.85em 2.1em",
              background: "linear-gradient(135deg, #bbb, #666)",
              color: "#222",
              fontWeight: 900,
              borderRadius: "2em",
              cursor: uploading ? "not-allowed" : "pointer",
              boxShadow:
                "inset 0 2px 8px rgba(255,255,255,0.16), 0 4px 14px #0003",
              fontSize: "1.08rem",
              letterSpacing: 0.1,
              textShadow: "1.5px 1.5px 5px #fff8, 0 1.5px 5px #222",
              border: "none",
              opacity: uploading ? 0.6 : 1,
              pointerEvents: uploading ? "none" : "auto",
            }}
// FILE: Quizmaster.js
// In the Step 2 JSX, replace the onClick handler of the "Skip Media Upload" button:
onClick={() => {
  if (uploading) return;
  const csvName =
    (selectedQuiz && selectedQuiz.name) ||
    (currentCsvName ? (currentCsvName.endsWith(".csv") ? currentCsvName : `${currentCsvName}.csv`) : null);
  if (csvName) {
    window.location.href = `/csvquizedit?file=${encodeURIComponent(csvName)}`;
  } else {
    appAlert("No quiz file selected!");
  }
}}

            disabled={uploading}
          >
            Skip Media Upload
          </button>
        </div>
        {progress > 0 && progress < 1 && (
          <div style={{ marginTop: 14 }}>
            Uploading... {Math.round(progress * 100)}%
          </div>
        )}
      </div>
    </div>
  );
}



// ---- UI Step 3: Quiz Controls ----
const q = questions[current]; // <-- ADD THIS LINE

// AFTER (paste this in place of the old early-return)
// === Gate the Scores: +3s dwell on underlying slide, and lock auto-advance while we prepare ===





if (step === 3) {
  return (
          <>
      {csvTeamChoiceOffWarn && (
        <div
          style={{
            position: "fixed",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200100,
            maxWidth: "min(720px, 94vw)",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(180,120,0,0.95)",
            color: "#111",
            fontWeight: 700,
            fontSize: 14,
            boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
          }}
        >
          This quiz has STARTROUND rows with #TeamChoice / #PlayerChoice, but team round tag choice is
          turned off in Options. Those rows behave as normal STARTROUND slides.
        </div>
      )}
      {getTeamRoundChoiceSettingsFromLocalStorage().enabled &&
        teamTagPreflight &&
        Array.isArray(teamTagPreflight.warnings) &&
        teamTagPreflight.warnings.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: csvTeamChoiceOffWarn ? 56 : 8,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 200099,
              maxWidth: "min(720px, 94vw)",
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(200,60,60,0.92)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Tag pool preflight</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {teamTagPreflight.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      {tab === "questions" && (
               <QuizScreen
          q={questions[current]}
          timer={timer}
          timerActive={timerActive}
          allAnswers={allAnswers}
          revealedIndexes={revealedIndexes}
          vanishingIndexes={vanishingIndexes}
          justVanished={justVanished}
          current={current}
          questions={questions}
          gameCode={gameCode}
          joinCode={joinCode}
          teamInfo={teamInfo}
          roundQuestionStartIdx={roundQuestionStartIdx}
          advance={advance}
          editMode={editMode}
          autoPaused={autoPaused}
          autoPlayOn={autoPlayOn}
          onAnswersGateOpened={handleAnswersGateOpened}
          hideNearestAnswerBars={["NEARESTANSWER", "JACKPOT"].includes(String((questions[current]?.type ?? questions[current]?.CAT ?? "")).toUpperCase())}
          showQuizOptions={showQuizOptions}
          setShowQuizOptions={setShowQuizOptions}
          // Pass down editing handlers
          onEditChange={(field, value) => {
            const newQuestions = [...questions];
            if (field.startsWith("answers[")) {
              const idx = parseInt(field.match(/\[(\d+)\]/)[1], 10);
              if (!Array.isArray(newQuestions[current].answers)) {
                newQuestions[current].answers = [];
              }
              while (newQuestions[current].answers.length <= idx) {
                newQuestions[current].answers.push("");
              }
              newQuestions[current].answers[idx] = value;
            } else {
              newQuestions[current][field] = value;
            }
            setQuestions(newQuestions);
          }}
          onDoneEditing={async () => {
            setEditMode(false);
            setStep(1);
          }}
        />
      )}

      {/* Main scores view */}
      {tab === "scores" &&
        (selfiesReady ? (
          <Scores numQuestions={questions.length} gameCode={gameCode} />
        ) : (
          <div className="quiz-bg" style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div className="question-prompt" style={{maxWidth:800}}>Loading team photos…</div>
          </div>
        ))}

{/* Scores Overlay (conditionally rendered on top of the above views) */}
      {showQuestionScores && lastQuestionIdx !== null && (
        <DelayedQuestionScoresGate 
          show={true}
          idx={lastQuestionIdx}
          gameCode={gameCode}
          questions={questions} // <--- ADDED PROP
          roundQuestionStartIdx={roundQuestionStartIdx} // <--- ADDED PROP
          onCloseAndAdvance={() => {
            // This just hides the overlay. The main app will advance automatically.
            setShowQuestionScores(false);
          }}
        />
      )}

      {/* Answer Reveal Overlay for MULTICHOICE */}
      {showAnswerReveal && (
        <AnswerReveal
          q={questions[current]}
          questionIdx={current}
          gameCode={gameCode}
          onClose={() => {
            setShowAnswerReveal(false);

            // Only auto-advance after answer reveal if autoplay is ON.
            // When paused (opt_autoplay = "No"), stay on this question and let the host move manually.
            try {
              const autoSetting =
                (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase();
              const autoOn = autoSetting === "YES";
              if (autoOn) {
                advance(1, true); // Advance to the next question
              } else {
                // eslint-disable-next-line no-console
                console.log(
                  "[Quizmaster] AnswerReveal closed while auto-advance paused; not advancing."
                );
              }
            } catch {
              // If localStorage fails, fall back to current behavior
              advance(1, true);
            }
          }}
        />
      )}
    </>
  );
}




return (
  <>
    <TopTeamsPopup  //this is handled in a seperate file
      showTopTeams={showTopTeams}
      scrollRef={scrollRef}
      rowWidth={rowWidth}
      sortedTeams={sortedTeams}
      doubledTopTeams={doubledTopTeams}
      teamInfo={teamInfo}
    />
        {tab === "questions" && (
      <QuizScreen
        q={q}
        timer={timer}
        allAnswers={allAnswers}
        revealedIndexes={revealedIndexes}
        vanishingIndexes={vanishingIndexes}
        justVanished={justVanished}
        current={current}
        questions={questions}
        gameCode={gameCode}
        joinCode={joinCode}
        teamInfo={teamInfo}
        roundQuestionStartIdx={roundQuestionStartIdx}
        advance={advance}
        autoPaused={autoPaused}
        autoPlayOn={autoPlayOn}
      hideNearestAnswerBars={["NEARESTANSWER", "JACKPOT"].includes(String((q?.type ?? q?.CAT ?? "")).toUpperCase())}
      showQuizOptions={showQuizOptions}
      setShowQuizOptions={setShowQuizOptions}
      />
    )}




{tab === "scores" && (
  selfiesReady ? (
   <Scores numQuestions={questions.length} gameCode={gameCode} allTeamNames={Object.keys(teamInfo || {})} />

  ) : (
    <div className="quiz-bg" style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div className="question-prompt" style={{maxWidth:800}}>Loading team photos…</div>
    </div>
  )
)}


    {/* Add other tabs or navigation as needed */}



    {/* Add other tabs or navigation as needed */}

    <audio
      ref={pingRef}
      src="/EndQuestionPing.wav"
      preload="auto"
      style={{ display: "none" }}
    />
  </>







); // <----- THIS closes the last return









} // <----- THIS CLOSES THE FUNCTION. IT IS THE LAST CHARACTER IN THE FILE.
